// ╔══════════════════════════════════════════════════════════════════╗
// ║  PESACHOVÝ SEDER — interaktivní průvodce večerem                ║
// ║  Architektura: HOST (TV/PC) + PLAYER (mobil)                    ║
// ║  Sync: mock vrstva připravená na Firebase Realtime DB            ║
// ╚══════════════════════════════════════════════════════════════════╝

import { useState, useEffect, useRef, useReducer, useCallback, createContext, useContext } from "react";
import { db } from "./firebase";
import { ref, set, push, onValue, update, onDisconnect, remove } from "firebase/database";

// ═══════════════════════════════════════════════════════════
// 1. GLOBÁLNÍ STYLY
// ═══════════════════════════════════════════════════════════
const GLOBAL_STYLE = `
  @import url('https://fonts.googleapis.com/css2?family=Cinzel:wght@400;600;700;900&family=Lora:ital,wght@0,400;0,500;1,400;1,600;0,600;1,400&family=Crimson+Pro:ital,wght@0,300;0,400;1,400&display=swap');
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  html, body, #root { height: 100%; overflow: hidden; }
  body { overflow-x: hidden; font-family: 'Lora', serif; }

  @keyframes fadeSlideUp   { from{opacity:0;transform:translateY(32px)} to{opacity:1;transform:translateY(0)} }
  @keyframes fadeIn        { from{opacity:0} to{opacity:1} }
  @keyframes popIn         { 0%{transform:scale(0.75);opacity:0} 65%{transform:scale(1.05)} 100%{transform:scale(1);opacity:1} }
  @keyframes wordPop       { from{transform:scale(0) rotate(-8deg);opacity:0} to{transform:scale(1) rotate(0deg);opacity:1} }
  @keyframes starFloat     { 0%,100%{opacity:0.04;transform:translateY(0)} 50%{opacity:0.09;transform:translateY(-8px)} }
  @keyframes goldShimmer   { 0%{background-position:200% center} 100%{background-position:-200% center} }
  @keyframes reactionPulse { 0%,100%{transform:scale(1);box-shadow:0 0 0 0 rgba(220,38,38,0.5)} 50%{transform:scale(1.04);box-shadow:0 0 0 22px rgba(220,38,38,0)} }
  @keyframes breathe       { 0%,100%{opacity:0.7;transform:scale(1)} 50%{opacity:1;transform:scale(1.015)} }
  @keyframes slideUp       { from{transform:translateY(100%);opacity:0} to{transform:translateY(0);opacity:1} }
  @keyframes connectedPop  { 0%{transform:translateY(12px) scale(0.9);opacity:0} 100%{transform:translateY(0) scale(1);opacity:1} }
  @keyframes lobbyStar     { 0%,100%{opacity:0.02} 50%{opacity:0.07} }

  .anim-step    { animation: fadeSlideUp 0.7s cubic-bezier(0.16,1,0.3,1) both; }
  .anim-fade    { animation: fadeIn 0.4s ease both; }
  .anim-pop     { animation: popIn 0.45s cubic-bezier(0.34,1.56,0.64,1) both; }
  .anim-word    { animation: wordPop 0.38s cubic-bezier(0.34,1.56,0.64,1) both; }
  .anim-slide   { animation: slideUp 0.5s cubic-bezier(0.16,1,0.3,1) both; }
  .anim-conn    { animation: connectedPop 0.4s cubic-bezier(0.34,1.56,0.64,1) both; }
  .reaction-anim { animation: reactionPulse 0.85s ease-in-out infinite; }
  .breathe      { animation: breathe 3s ease-in-out infinite; }

  .gold-text {
    background: linear-gradient(90deg,#C9A84C 0%,#F5D87A 35%,#C9A84C 55%,#E8C878 75%,#C9A84C 100%);
    background-size: 200% auto;
    -webkit-background-clip: text; -webkit-text-fill-color: transparent;
    background-clip: text;
    animation: goldShimmer 4s linear infinite;
  }

  /* Scrollbar */
  ::-webkit-scrollbar { width: 5px; }
  ::-webkit-scrollbar-track { background: rgba(0,0,0,0.08); }
  ::-webkit-scrollbar-thumb { background: rgba(201,168,76,0.35); border-radius: 3px; }

  /* Touch feedback */
  .touch-btn { -webkit-tap-highlight-color: transparent; user-select: none; }
  .touch-btn:active { transform: scale(0.95) !important; }
`;

// ═══════════════════════════════════════════════════════════
// 2. KONSTANTY
// ═══════════════════════════════════════════════════════════
const QUIZ_COLORS = [
  { bg:"#2563EB", active:"#1D4ED8", label:"A" },
  { bg:"#DC2626", active:"#B91C1C", label:"B" },
  { bg:"#16A34A", active:"#15803D", label:"C" },
  { bg:"#D97706", active:"#B45309", label:"D" },
];

const BIBLICAL_CHARS = [
  { name:"Mojžíš",   emoji:"🧙" },
  { name:"Miriam",   emoji:"💃" },
  { name:"Adam",     emoji:"🌿" },
  { name:"Eva",      emoji:"🍎" },
  { name:"Noe",      emoji:"🚢" },
  { name:"Abraham",  emoji:"⛺" },
  { name:"Sára",     emoji:"👵" },
  { name:"Izák",     emoji:"🐑" },
  { name:"Jákob",    emoji:"🪨" },
  { name:"Ráchel",   emoji:"🐚" },
  { name:"Josef",    emoji:"🎨" },
  { name:"Ester",    emoji:"👑" },
  { name:"Rut",      emoji:"🌾" },
  { name:"David",    emoji:"🎵" },
  { name:"Šalamoun", emoji:"💎" },
  { name:"Jonáš",    emoji:"🐋" },
  { name:"Elia",     emoji:"🔥" },
  { name:"Petr",     emoji:"🎣" },
];

const ACTIVITY_LABELS = {
  kvíz:"🎯 Kvíz", postřeh:"⚡ Postřeh",
  seřazování:"🔀 Seřazování", reflexe:"💭 Reflexe", anketa:"💬 Anketa",
  hlasování:"🗳️ Hlasování", psaní:"⌨️ Psaní", pohár:"🍷 Pohár",
};

// URL aplikace pro QR kód — automaticky detekuje produkční i dev prostředí
const APP_URL = typeof window !== "undefined"
  ? `${window.location.protocol}//${window.location.host}${window.location.pathname.replace(/\/$/, "")}`
  : "https://seder.senkpatrik.cz";

// ═══════════════════════════════════════════════════════════
// 3. SEDER DATA — 15 kroků
// ═══════════════════════════════════════════════════════════
// Pravidla aktivit: 1× Wordcloud, 1× Reflexe, 1× Škála, 1× Postřeh,
// Kvízy a Seřazování libovolněkrát, + 2 nové typy: Hlasování, Psaní
const SEDER = [
  // ============================================================
  // ÚVODNÍ ČÁST (HISTORIE, SYMBOLY, PRAVIDLA)
  // ============================================================
  {
    id: 1, emoji: "🕯️",
    nazev: "Vítejte u Sederu", podnadpis: "Noc vykoupení",
    introText: "Každý rok v období Velikonoc slaví židovský národ Pesach, aby si připomněl vysvobození z egyptského otroctví. Pro nás křesťany je to připomínka Poslední večeře Ježíše Krista, který dal tomuto řádu nový smysl své oběti.",
    citaceHagady: "Tato noc patří Hospodinu a mají ji dodržovat všichni synové Izraele po všechna svá pokolení.",
    instrukceProHrace: "Dnes večer jsme přímými účastníky příběhu o záchraně a svobodě.",
    typAktivity: "kvíz",
    aktivity: { typ: "quiz", otazky: [
      { otazka: "Proč se všeobecně slaví seder?", moznosti: ["Kvůli blbci Jidášovi", "Protože nemáme nic jinšího na práci", "Abychom slavili", "Aby se opět sešla stará dobrá parta"], spravna: 2, body: 10 }
    ]}
  },
  {
    id: 2, emoji: "📜",
    nazev: "Historický kontext", podnadpis: "Z Egypta do Jeruzaléma",
    introText: "Před 3500 lety Bůh skrze Mojžíše vyvedl svůj lid z otroctví. O staletí později slavil tuto večeři Ježíš se svými učedníky. Právě zde vzal maces a víno a ustanovil Večeři Páně. Svátek nekvašených chlebů seoslavuje sedm dní.",
    zajimavost: "Slovo Pesach znamená 'přeskočení' – Hospodin při 10. egypské ráně pobil všechno prvorozené a přitom přeskočil obydlí dětí Izraele.",
    instrukceProHrace: "Dnes večer jsme přímými účastníky příběhu o záchraně a svobodě.",
    typAktivity: "kvíz",
    aktivity: { typ: "quiz", otazky: [
      { otazka: "Jak se jmenoval farao, který odmítal propustit Izraelce?", moznosti: ["Tutanchamon", "Cheops", "Achnaton", "Ramesse II."], spravna: 3, body: 10 }
    ]}
  },
  {
    id: 3, emoji: "🛋️",
    nazev: "Pravidla svobodných", podnadpis: "Proč jíme v záklonu?",
    introText: "Seder znamená 'řád'. Důležitým pravidlem je opírání se na levý bok. Otroci jedli v spěchu a vestoje, ale svobodní lidé hodují v klidu. Dnes jsme všichni svobodní, proto budeme hodovat jako králové.",
    zajimavost: "Pro Židy je po celý svátek zákaz vlastnění, přechovávání a i používání potravin, které buď podléhají zkvašení nebo mají povahu kvasu.",
    instrukceProHrace: "Budeme jíst jako králové, ovšem teď si dáme aktivitu, která je spíše pro otroky ve střehu a spěchu.",
    typAktivity: "postřeh",
    aktivity: { typ: "reaction", instrukce: "Až budete na mobilu vyzváni, stiskněte tlačítko - nejrychlejší otrok vyhrává!", body: 10 }
  },
  {
    id: 4, emoji: "🍽️",
    nazev: "Sederová mísa", podnadpis: "Symboly na stole",
    introText: "Mísa obsahuje: Slanou vodu (slzy), Maror (hořkost), Charoset (maltu na cihly), Karpas (zeleninu jako naději), Zeroa (kost jako beránka) a Bejca (vejce jako nový život).",
    zajimavost: "Tři macesy na stole představují tři skupiny Božího lidu: kněze, levity a lid Izraele. Také tři části židovského národa: Koheny, Levity a Izraelity.",
    instrukceProHrace: "Dnes večer jsme přímými účastníky příběhu o záchraně a svobodě.",
    typAktivity: "kvíz",
    aktivity: { typ: "quiz", otazky: [
      { otazka: "Tři macesy jsou na připomínku tří praotců, kteří to jsou?", moznosti: ["Abrahám, Izák a Jákob", "Adam, Abrahám a Izák", "Abrahám, Izák a Noe", "Abrahám, Jákob a Mojžíš"], spravna: 0, body: 10 },
      { otazka: "Zeroa je co?", moznosti: ["Hořké byliny", "Vejce", "Maso", "Slaná voda"], spravna: 2, body: 10 }
    ]}
  },

  // ============================================================
  // RITUÁLNÍ KROKY 1-14 (DLE PDF)
  // ============================================================
  {
    id: 5, emoji: "🍷",
    nazev: "1. KADEŠ (Posvěcení)", podnadpis: "První pohár vína",
    introText: "Naplníme první pohár. Tento pohár připomíná Boží slib: 'Vyvedu vás z egyptské roboty'.",
    citaceHagady: "Požehnaný jsi Hospodine, Bože náš, Králi světa, stvořiteli plodu vinné révy. Požehnaný jsi, Hospodine, Bože náš, Králi světa, jenž nás vyvolil a vyvýšil mezi všemi národy a vyzdvihl nás nad všechny jazyky a jenž nás posvětil svými příkazy. Ve své lásce jsi nám dal, Hospodine, Bože náš, svátky radosti a veselé sváteční chvíle, tento den svátku nekvašených chlebů, tento nádherný den posvátného shromáždění, svátek naší svobody, na památku odchodu z Egypta. Vpravdě jsi nás vyvolil a posvětil mezi všemi národy a ve své lásce jsi nám dal své svaté svátky pro radost a veselí. Požehnaný jsi, Hospodine, jenž posvěcuješ (svůj lid) Izrael a svátky",     instrukceProHrace: "Povstaňte k požehnání a vypijte první pohár.",
    typAktivity: "info"
  },
  {
    id: 6, emoji: "👐",
    nazev: "2. URCHAC (Umytí)", podnadpis: "První očištění",
    introText: "Umýváme si ruce bez požehnání, neboť ještě nenastal čas k jídlu. Je to příprava na první dotyk se svatým jídlem.",
    citaceHagady: "Hospodine, zkoumáš mě a znáš mě. Víš o mně, ať sedím nebo vstanu, z dálky je ti jasné, co chci dělat (Žalm 139).",
    typAktivity: "info"
  },
  {
    id: 7, emoji: "🌿",
    nazev: "3. KARPAS (Zelenina)", podnadpis: "Chuť slz a jara",
    introText: "Namočíme zeleninu do slané vody. Zeleň je symbolem jara, slaná voda připomíná slzy prolité v otroctví.",
    citaceHagady: "Požehnaný jsi, Hospodine, Bože náš, Králi světa, stvořiteli plodů země.",
    instrukceProHrace: "Všichni okusí zeleninu namočenou v misce se slanou vodou.",
    typAktivity: "kvíz",
    aktivity: { typ: "quiz", otazky: [
      { otazka: "Co symbolizuje slaná voda?", moznosti: ["Rudé moře", "Slzy otroků", "Pot po aktivitě", "Nepovedený oběd"], spravna: 1, body: 15 }
    ]}
  },
  {
    id: 8, emoji: "🫓",
    nazev: "4. JACHAC (Rozdělení)", podnadpis: "Lámání macesu",
    introText: "Rozlomíme prostřední maces. Menší část vrátíme, větší (Afikoman) schováme. Je to chléb chudoby.",
    citaceHagady: "Toto je chléb nouze, který jedli naši předkové. Každý, kdo je hladov, ať přijde a pojí, každý, kdo je v nouzi, ať přijde a slaví Pesach.",
    typAktivity: "info"
  },
  {
    id: 9, emoji: "🗣️",
    nazev: "5. MAGID (Vyprávění)", podnadpis: "Příběh o svobodě",
    introText: "Nejmladší se ptá: „Proč je tato noc jiná, než všechny ostatní noci?“ „Že každou jinou noc jíme chléb kvašený i nekvašený, ale tuto noc jenom macesy?“ „Že každou jinou noc jíme všechnu zeleninu, ale dnešní noc pouze hořkou?“ „Že každou jinou noc nenamáčíme ani jednou do slané vody, ale tuto noc dvakrát?“ „Že každou jinou noc máme i nemáme pohodlí, ale tuto noc si libujeme všichni?“",
    citaceHagady: "Byli jsme v Egyptě otroky, ale Hospodin, náš Bůh, nás osvobodil silnou rukou.",
    instrukceProHrace: "Nalijeme druhý pohár, ale vypijeme až rabín povolí!",
    typAktivity: "info"
  },
  {
    id: 10, emoji: "🗣️",
    nazev: "5. MAGID (Vyprávění)", podnadpis: "Příběh o svobodě",
    citaceHagady: "Požehnaný jsi, Hospodine, Bože náš, Králi světa, jenž jsi nás vykoupil, že jsi vykoupil naše předky z Egypta, a nám dal dosáhnout této noci, abychom v ní jedli macesy a trpké byliny. Požehnaný jsi, Hospodine, Bože náš, Králi světa, stvořiteli vinné révy!",
    instrukceProHrace: "Pijeme druhý pohár.",
    typAktivity: "info"
  },
  {
    id: 11, emoji: "🧼",
    nazev: "6. ROCHCA (Mytí rukou)", podnadpis: "Duchovní čistota",
    introText: "Myjeme si ruce podruhé, tentokrát s požehnáním. Připravujeme se na jídlo macesů.",
    citaceHagady: "Požehnaný jsi, Hospodine, Bože náš, Králi světa, jenž jsi nás posvětil svými příkazy a uložil nám umývání rukou.",
    typAktivity: "info"
  },
  {
    id: 12, emoji: "🥨",
    nazev: "7. MOCI - MACA", podnadpis: "Požehnání nad macesy",
    introText: "Říkáme požehnání nad nekvašeným chlebem. Maces nestihl vykynout, symbolizuje spěch při odchodu.",
    citaceHagady: "Požehnaný jsi, Hospodine, Bože náš, Králi světa, že vyvádíš ze země chléb. Požehnaný jsi, Hospodine, Bože náš, Králi světa, že jsi nás posvětil svými příkazy a dal jsi nám příkaz o pojídání nekvašeného chleba.",
    instrukceProHrace: "Jíme první maces a pronášíme požehnání.",
    typAktivity: "info"
  },
  {
    id: 13, emoji: "🥬",
    nazev: "8. MAROR (Hořké byliny)", podnadpis: "Hořkost života",
    introText: "Jíme křen nebo salát. Máme pocítit, jak hořký byl život v nesvobodě.",
    citaceHagady: "Požehnaný jsi, Hospodine, Bože náš, Králi světa, jenž jsi nás posvětil svými příkazy a uložil nám jíst hořké byliny.",
    typAktivity: "info"
  },
  {
    id: 14, emoji: "🥪",
    nazev: "9. KORECH (Sendvič)", podnadpis: "Spojení protikladů",
    introText: "Spojíme maces, maror a charoset. Hořkost otroctví se mísí se sladkostí naděje.",
    typAktivity: "info"
  },
  {
    id: 15, emoji: "🍗",
    nazev: "10. ŠULCHAN ORECH", podnadpis: "Společná hostina",
    introText: "Nyní prostřeme stůl k hlavnímu jídlu. Začínáme vajíčkem, symbolem koloběhu života.",
    citaceHagady: "Kéž je vůle tvá, Hospodine, Bože náš, aby byl brzy vystavěn chrám v našich dnech.",
    typAktivity: "info"
  },
  {
    id: 16, emoji: "🔍",
    nazev: "11. CAFUN (Hledání)", podnadpis: "Tajemství Afikomanu",
    introText: "Večeře končí jídlem Afikomanu. Je to 'zákusek', po kterém už nesmí přijít žádná jiná chuť.",
    zajimavost: "Afikoman představuje velikonočního beránka.",
    typAktivity: "info"
  },
  {
    id: 17, emoji: "🙏",
    nazev: "12. BARECH (Díky)", podnadpis: "Třetí pohár",
    introText: "Děkujeme za pokrm. Pijeme třetí pohár – pohár vykoupení. Ježíš u něj řekl: 'Toto je má krev'.",
    citaceHagady: "Požehnaný jsi, Hospodine, Bože náš, Králi světa, jenž ve své dobrotě živíš celý svět milostí.",
    typAktivity: "anketa",
    aktivity: { typ: "reflection", otazka: "Za co u tohoto stolu nejvíce děkuješ?", body: 10 }
  },
  {
    id: 18, emoji: "🎶",
    nazev: "13. HALEL (Chvály)", podnadpis: "Písně radosti",
    introText: "Zpíváme žalmy a pijeme čtvrtý pohár. Otevíráme dveře proroku Elijášovi.",
    citaceHagady: "Hospodine, otevři mé rty, aby má ústa zvěstovala tvou chválu.",
    typAktivity: "info"
  },
  {
    id: 19, emoji: "✨",
    nazev: "14. NIRCA (Přijetí)", podnadpis: "Závěr řádu",
    introText: "Seder je u konce. Vše bylo vykonáno podle řádu. Kéž jsme příští rok v Jeruzalémě!",
    citaceHagady: "Příští rok v Jeruzalémě!",
    typAktivity: "info"
  },
  {
    id: 20, emoji: "🏁",
    nazev: "Konec večeře", podnadpis: "Pokoj vám",
    introText: "Děkujeme, že jste s námi prožili tento večer. Kéž vás příběh o svobodě provází i v dalších dnech.",
    citaceHagady: "Příští rok nashledanou v Jeruzalémě!",
    instrukceProHrace: "Můžete zůstat u stolu a dál spolu hovořit. Krásné Velikonoce!",
    typAktivity: "info"
  }
];

// ═══════════════════════════════════════════════════════════
// 4. HOOKS
// ═══════════════════════════════════════════════════════════

/** Detekce zařízení — vrací 'HOST' pro desktop, 'PLAYER' pro mobil */
function useDeviceRole() {
  const [role, setRole] = useState(null); // null = loading

  useEffect(() => {
    const ua = navigator.userAgent;
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua)
      || (navigator.maxTouchPoints > 1 && /Macintosh/i.test(ua)); // iPad detection
    setRole(isMobile ? "PLAYER" : "HOST");
  }, []);

  return role;
}

// ═══════════════════════════════════════════════════════════
// 5. SYNC ENGINE — Firebase-ready vrstva
// ═══════════════════════════════════════════════════════════
//
// FIREBASE INTEGRACE:
// Nahraď useMockSync funkcemi z firebase/database:
//   import { ref, set, onValue, push } from "firebase/database";
// Každá funkce níže má komentář // FIREBASE: ...
//
function useSyncEngine() {
  const [state, setState] = useState({
    phase: "lobby",
    players: [],
    stepIdx: 0,
    sederMode: "reading",
    gameActive: false,
    gameState: {
      answers: {}, questionIndex: 0,
      reactionActive: false, reactionWinner: null,
      revealed: false, scaleAnswers: {}, typingSubmissions: {},
    },
    submittedWords: [],
  });

  const reactionTimerRef = useRef(null);

  // ── Poslouchej CELOU databázi → synchronizuje VŠECHNY klienty ──
  useEffect(() => {
    const rootRef = ref(db, "/");
    const unsub = onValue(rootRef, (snapshot) => {
      const data = snapshot.val();
      if (!data) return;
      setState({
        phase:          data.phase         ?? "lobby",
        players:        data.players       ? Object.values(data.players) : [],
        stepIdx:        data.stepIdx       ?? 0,
        sederMode:      data.sederMode     ?? "reading",
        gameActive:     data.gameActive    ?? false,
        gameState:      data.gameState     ?? { answers:{}, questionIndex:0, reactionActive:false, reactionWinner:null, revealed:false, scaleAnswers:{}, typingSubmissions:{} },
        submittedWords: data.submittedWords ? Object.values(data.submittedWords) : [],
      });
    });
    return () => unsub();
  }, []);

  // ── Akce → píší do Firebase → onValue je automaticky broadcastuje všem ──

  const joinPlayer = useCallback((name, emoji) => {
    const id = `player_${Date.now()}_${Math.random().toString(36).slice(2,6)}`;
    set(ref(db, `players/${id}`), { id, name, emoji, score: 0, answered: false });
    return id;
  }, []);

  const startSeder = useCallback(() => {
    set(ref(db, "phase"), "playing");
  }, []);

  const goToStep = useCallback((idx) => {
    if (reactionTimerRef.current) clearTimeout(reactionTimerRef.current);
    update(ref(db), {
      stepIdx: idx,
      sederMode: "reading",
      gameActive: false,
      "gameState/answers": {},
      "gameState/questionIndex": 0,
      "gameState/reactionActive": false,
      "gameState/reactionWinner": null,
      "gameState/revealed": false,
      "gameState/scaleAnswers": {},
      "gameState/typingSubmissions": null,
      submittedWords: null,
    });
    // Reset answered pro všechny hráče
    setState(prev => {
      prev.players.forEach(p => set(ref(db, `players/${p.id}/answered`), false));
      return prev;
    });
  }, []);

  const setMode = useCallback((mode) => {
    set(ref(db, "sederMode"), mode);
  }, []);

  const startGame = useCallback(() => {
    set(ref(db, "gameActive"), true);
    const step = SEDER[state.stepIdx];
    if (step?.typAktivity === "postřeh") {
      const delay = 2500 + Math.random() * 4000;
      reactionTimerRef.current = setTimeout(() => {
        set(ref(db, "gameState/reactionActive"), true);
      }, delay);
    }
  }, [state.stepIdx]);

  const revealAnswers = useCallback(() => {
    if (reactionTimerRef.current) clearTimeout(reactionTimerRef.current);
    update(ref(db, "gameState"), { revealed: true, reactionActive: false });
    set(ref(db, "gameActive"), false);
  }, []);

  const nextQuestion = useCallback(() => {
    update(ref(db, "gameState"), {
      questionIndex: (state.gameState.questionIndex || 0) + 1,
      answers: null,
      revealed: false,
    });
    set(ref(db, "submittedWords"), null);
    state.players.forEach(p => set(ref(db, `players/${p.id}/answered`), false));
  }, [state.gameState.questionIndex, state.players]);

  const addScore = useCallback((playerId, pts) => {
    const player = state.players.find(p => p.id === playerId);
    if (!player) return;
    set(ref(db, `players/${playerId}/score`), (player.score || 0) + pts);
  }, [state.players]);

  const submitQuizAnswer = useCallback((playerId, answerIdx, isCorrect, pts) => {
    const player = state.players.find(p => p.id === playerId);
    if (!player || player.answered) return; // zabránění dvojího bodování
    update(ref(db, "gameState/answers"), { [playerId]: answerIdx });
    set(ref(db, `players/${playerId}/answered`), true);
    if (isCorrect) addScore(playerId, pts);
  }, [addScore, state.players]);

  const submitWord = useCallback((playerId, text, pts) => {
    const player = state.players.find(p => p.id === playerId);
    if (!player || player.answered) return;
    push(ref(db, "submittedWords"), text);
    set(ref(db, `players/${playerId}/answered`), true);
    addScore(playerId, pts);
  }, [addScore, state.players]);

  const submitScale = useCallback((playerId, scaleIdx, label, pts) => {
    const player = state.players.find(p => p.id === playerId);
    if (!player || player.answered) return;
    update(ref(db, "gameState/scaleAnswers"), { [playerId]: scaleIdx });
    set(ref(db, `players/${playerId}/answered`), true);
    addScore(playerId, pts);
  }, [addScore, state.players]);

  const submitReaction = useCallback((playerId) => {
    const player = state.players.find(p => p.id === playerId);
    if (!player || !state.gameState.reactionActive || state.gameState.reactionWinner) return;
    if (reactionTimerRef.current) clearTimeout(reactionTimerRef.current);
    const step = SEDER[state.stepIdx];
    const pts = step?.aktivity?.body || 20;
    update(ref(db, "gameState"), { reactionWinner: player.name, reactionActive: false });
    set(ref(db, `players/${playerId}/answered`), true);
    addScore(playerId, pts);
  }, [state.players, state.gameState, state.stepIdx, addScore]);

  const submitSorting = useCallback((playerId, score) => {
    const player = state.players.find(p => p.id === playerId);
    if (!player || player.answered) return;
    set(ref(db, `players/${playerId}/answered`), true);
    addScore(playerId, score);
  }, [addScore, state.players]);

  const CHALICE_POINTS = [30, 25, 20, 15, 10];
  const CHALICE_MAX = 5;

  const submitChalice = useCallback((playerId) => {
    const player = state.players.find(p => p.id === playerId);
    if (!player || player.answered) return;
    const existingCount = Object.keys(state.gameState.typingSubmissions || {}).length;
    if (existingCount >= CHALICE_MAX) return;
    const pts = CHALICE_POINTS[existingCount] || 0;
    push(ref(db, "gameState/typingSubmissions"), playerId);
    set(ref(db, `players/${playerId}/answered`), true);
    if (pts > 0) addScore(playerId, pts);
    if (existingCount + 1 >= CHALICE_MAX) {
      update(ref(db, "gameState"), { revealed: true });
      set(ref(db, "gameActive"), false);
    }
  }, [addScore, state.players, state.gameState.typingSubmissions]);

  const submitTyping = useCallback((playerId, word) => {
    const player = state.players.find(p => p.id === playerId);
    if (!player || player.answered) return;
    const step = SEDER[state.stepIdx];
    const correctWord = step.aktivity?.slovo?.toUpperCase();
    if (!word.trim().toUpperCase().includes(correctWord)) return;
    const existingCount = Object.keys(state.gameState.typingSubmissions || {}).length;
    const fullPts = step.aktivity?.body || 30;
    const pts = Math.max(Math.round(fullPts * 0.3), fullPts - existingCount * 5);
    push(ref(db, "gameState/typingSubmissions"), playerId);
    set(ref(db, `players/${playerId}/answered`), true);
    addScore(playerId, pts);
  }, [addScore, state.players, state.gameState.typingSubmissions, state.stepIdx]);

  return {
    state,
    actions: { joinPlayer, startSeder, goToStep, setMode, startGame, revealAnswers, nextQuestion, submitQuizAnswer, submitWord, submitScale, submitReaction, submitSorting, submitTyping, submitChalice },
  };
}


// ═══════════════════════════════════════════════════════════
// 6. SHARED HELPERS
// ═══════════════════════════════════════════════════════════
const shuffleArr = (arr) => {
  const a = [...arr];
  for (let i = a.length-1; i>0; i--) {
    const j = Math.floor(Math.random()*(i+1));
    [a[i],a[j]]=[a[j],a[i]];
  }
  return a;
};

// ═══════════════════════════════════════════════════════════
// 7. SHARED UI COMPONENTS
// ═══════════════════════════════════════════════════════════

function TVStarfield({ count=60 }) {
  const stars = useRef(Array.from({length:count}, () => ({
    x: Math.random()*100, y: Math.random()*100,
    size: Math.random()>0.85 ? 2 : 1,
    dur: 3+Math.random()*5, delay: Math.random()*6,
  }))).current;

  return (
    <div style={{ position:"fixed", inset:0, pointerEvents:"none", overflow:"hidden", zIndex:0 }}>
      {stars.map((s,i) => (
        <div key={i} style={{
          position:"absolute", left:`${s.x}%`, top:`${s.y}%`,
          width:s.size, height:s.size, borderRadius:"50%", background:"white",
          animation:`starFloat ${s.dur}s ease-in-out infinite`, animationDelay:`${s.delay}s`,
        }}/>
      ))}
    </div>
  );
}

function OrnamentalDivider({ color="#C9A84C", size="full" }) {
  const w = size==="sm" ? 80 : undefined;
  return (
    <div style={{ display:"flex", alignItems:"center", gap:10 }}>
      <div style={{ flex:1, maxWidth:w, height:1, background:`linear-gradient(to right, transparent, ${color}55)` }}/>
      <div style={{ display:"flex", gap:5, alignItems:"center" }}>
        {[4,7,4].map((s,i)=><div key={i} style={{ width:s, height:s, borderRadius:"50%", background:color, opacity:i===1?0.8:0.4 }}/>)}
      </div>
      <div style={{ flex:1, maxWidth:w, height:1, background:`linear-gradient(to left, transparent, ${color}55)` }}/>
    </div>
  );
}

function ScoreboardOverlay({ players, onClose }) {
  const sorted = [...players].sort((a,b)=>b.score-a.score);
  return (
    <div className="anim-fade" style={{ position:"fixed", inset:0, background:"rgba(4,10,40,0.93)", zIndex:3000, display:"flex", alignItems:"center", justifyContent:"center", padding:20, backdropFilter:"blur(16px)" }}>
      <div className="anim-pop" style={{ background:"linear-gradient(145deg,#1A2D6B,#0D1B4B)", border:"2px solid #C9A84C", borderRadius:22, padding:"36px 28px", width:"100%", maxWidth:480, maxHeight:"90vh", overflowY:"auto", boxShadow:"0 0 80px rgba(201,168,76,0.3)" }}>
        <div style={{ textAlign:"center", marginBottom:28 }}>
          <div style={{ fontSize:48, marginBottom:8 }}>🏆</div>
          <h2 style={{ fontFamily:"Cinzel, serif", color:"#E8C878", fontSize:22, fontWeight:700 }}>Průběžné pořadí</h2>
        </div>
        {sorted.map((p,idx)=>(
          <div key={p.id} style={{ display:"flex", alignItems:"center", gap:14, padding:"13px 16px", marginBottom:8, borderRadius:12,
            background:idx===0?"rgba(201,168,76,0.18)":"rgba(255,255,255,0.04)",
            border:`1px solid ${idx===0?"rgba(201,168,76,0.5)":"rgba(255,255,255,0.07)"}` }}>
            <span style={{ fontSize:26, width:32, textAlign:"center" }}>{["🥇","🥈","🥉"][idx]||`${idx+1}.`}</span>
            <span style={{ fontSize:22 }}>{p.emoji}</span>
            <span style={{ fontFamily:"Lora, serif", color:"#F5E6C8", fontSize:17, flex:1 }}>{p.name}</span>
            <span style={{ fontFamily:"Cinzel, serif", color:"#E8C878", fontSize:20, fontWeight:700 }}>{p.score}</span>
            <span style={{ color:"#C9A84C88", fontSize:13, fontFamily:"Lora, serif" }}>b.</span>
          </div>
        ))}
        <button onClick={onClose} className="touch-btn" style={{ display:"block", width:"100%", marginTop:24, padding:"16px", background:"linear-gradient(135deg,#C9A84C,#8B6914)", border:"none", borderRadius:12, fontFamily:"Cinzel, serif", fontSize:16, color:"#0D1B4B", cursor:"pointer", fontWeight:700, letterSpacing:1 }}>
          Zavřít ✕
        </button>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// 8. TV / HOST KOMPONENTY
// ═══════════════════════════════════════════════════════════


function TVLobby({ players, onStart, onlineMap = {} }) {
  return (
    <div style={{ height:"100%", display:"flex", flexDirection:"column", padding:"3vh 4vw" }}>
      <TVStarfield />
      <div style={{ position:"relative", zIndex:1, flex:1, display:"flex", flexDirection:"column" }}>
        {/* Title */}
        <div style={{ textAlign:"center", marginBottom:"3vh" }}>
          <div style={{ fontSize:"clamp(14px,2vw,22px)", fontFamily:"Cinzel, serif", color:"#C9A84C", letterSpacing:8, marginBottom:8, opacity:0.8 }}>
            ✡️ PESACHOVÝ SEDER ✡️
          </div>
          <h1 className="gold-text" style={{ fontFamily:"Cinzel, serif", fontSize:"clamp(32px,5vw,68px)", fontWeight:900, lineHeight:1.1, marginBottom:12 }}>
            Večer svobody
          </h1>
          <OrnamentalDivider />
        </div>

        {/* Two-column lobby */}
        <div style={{ flex:1, display:"grid", gridTemplateColumns:"1fr 1fr", gap:"3vw", alignItems:"start" }}>

          {/* LEFT — QR kódy */}
          <div style={{ display:"flex", flexDirection:"column", gap:"2vh" }}>
            {/* Wi-Fi QR */}
            <div style={{ background:"rgba(255,255,255,0.04)", border:"1px solid rgba(201,168,76,0.25)", borderRadius:16, padding:"3vh 2vw", textAlign:"center" }}>
              <div style={{ fontFamily:"Cinzel, serif", color:"#C9A84C", fontSize:"clamp(10px,1.2vw,14px)", letterSpacing:4, marginBottom:"2vh" }}>
                📶 WI-FI SÍŤ
              </div>
              <img src="/seder/QR_wifi.jpg" alt="Wi-Fi QR kód" style={{ width:160, height:160, borderRadius:8, display:"block", margin:"0 auto 12px", background:"white", padding:4 }} />
              <div style={{ fontFamily:"Lora, serif", fontSize:"clamp(12px,1.1vw,15px)", color:"rgba(245,230,200,0.6)", fontStyle:"italic" }}>
                Nejdřív se připoj na Wi-Fi
              </div>
            </div>

            {/* Game URL QR */}
            <div style={{ background:"rgba(255,255,255,0.04)", border:"1px solid rgba(201,168,76,0.3)", borderRadius:16, padding:"3vh 2vw", textAlign:"center" }}>
              <div style={{ fontFamily:"Cinzel, serif", color:"#C9A84C", fontSize:"clamp(10px,1.2vw,14px)", letterSpacing:4, marginBottom:"2vh" }}>
                🎮 NASKENUJ A HRAJ
              </div>
              <img src="/seder/QR_web.png" alt="QR kód hry" style={{ width:160, height:160, borderRadius:8, display:"block", margin:"0 auto 12px", background:"white", padding:4 }} />
              <div style={{ fontFamily:"Lora, serif", fontSize:"clamp(10px,0.9vw,12px)", color:"rgba(245,230,200,0.35)", marginTop:4 }}>{APP_URL}</div>
            </div>
          </div>

          {/* RIGHT — Připojení hráči + start */}
          <div style={{ display:"flex", flexDirection:"column", gap:"2vh" }}>
            <div style={{ background:"rgba(255,255,255,0.04)", border:"1px solid rgba(201,168,76,0.25)", borderRadius:16, padding:"2.5vh 2vw", flex:1 }}>
              <div style={{ fontFamily:"Cinzel, serif", color:"#C9A84C", fontSize:"clamp(10px,1.2vw,14px)", letterSpacing:4, marginBottom:"2vh", textAlign:"center" }}>
                👥 PŘIPOJENÍ HRÁČI ({players.length}/10)
              </div>

              {players.length === 0 ? (
                <div style={{ textAlign:"center", padding:"4vh 0", opacity:0.4 }}>
                  <div style={{ fontSize:40, marginBottom:8 }}>⏳</div>
                  <div style={{ fontFamily:"Lora, serif", color:"#F5E6C8", fontSize:"clamp(13px,1.1vw,16px)", fontStyle:"italic" }}>
                    Čeká se na hráče…<br/>Naskenujte QR kód →
                  </div>
                </div>
              ) : (
                <div style={{ display:"flex", flexWrap:"wrap", gap:10 }}>
                  {players.map((p,i)=>(
                    <div key={p.id} className="anim-conn" style={{ animationDelay:`${i*0.07}s`, display:"flex", alignItems:"center", gap:8, padding:"8px 16px", background:"rgba(201,168,76,0.12)", border:"1px solid rgba(201,168,76,0.4)", borderRadius:24 }}>
                      <span style={{ fontSize:22 }}>{p.emoji}</span>
                      <span style={{ fontFamily:"Lora, serif", color:"#F5E6C8", fontSize:"clamp(13px,1.1vw,16px)" }}>{p.name}</span>
                      <div style={{ width:7, height:7, borderRadius:"50%", background:onlineMap[p.id]?"#4ADE80":"#EF4444", boxShadow:onlineMap[p.id]?"0 0 6px #4ADE80":"none" }}/>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Start tlačítko */}
            <button onClick={onStart} disabled={players.length<1} className="touch-btn" style={{
              padding:"clamp(14px,2vh,24px)", borderRadius:16, border:"2px solid #C9A84C",
              background: players.length>=1 ? "linear-gradient(135deg,#C9A84C,#8B6914)" : "rgba(255,255,255,0.05)",
              color: players.length>=1 ? "#0D1B4B" : "rgba(255,255,255,0.25)",
              cursor: players.length>=1 ? "pointer" : "not-allowed",
              fontFamily:"Cinzel, serif", fontSize:"clamp(14px,1.8vw,22px)", fontWeight:900,
              letterSpacing:2, transition:"all 0.2s",
              boxShadow: players.length>=1 ? "0 8px 32px rgba(201,168,76,0.4)" : "none",
            }}>
              {players.length<1 ? "⏳ Čeká se na hráče..." : `🕍 Zahájit Seder (${players.length} hráčů)`}
            </button>

            <div style={{ textAlign:"center", fontFamily:"Lora, serif", fontSize:"clamp(11px,1vw,13px)", color:"rgba(245,230,200,0.35)", fontStyle:"italic" }}>
              Admin panel: Ctrl+A • Přepnout krok: ◀ ▶
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function TVProgressBar({ current, total }) {
  return (
    <div style={{ padding:"12px 40px 0", display:"flex", gap:3 }}>
      {Array.from({length:total}).map((_,i)=>(
        <div key={i} style={{ flex:1, height:i===current?5:2, borderRadius:3, transition:"all 0.5s ease",
          background:i<current?"#C9A84C":i===current?"#E8C878":"rgba(255,255,255,0.07)",
          boxShadow:i===current?"0 0 10px rgba(232,200,120,0.7)":"none" }}/>
      ))}
    </div>
  );
}

function TVHeader({ step, stepIdx, total }) {
  return (
    <div style={{ textAlign:"center", padding:"2vh 5vw 1vh" }}>
      <div style={{ display:"flex", alignItems:"center", justifyContent:"center", gap:10, marginBottom:10 }}>
        <div style={{ height:1, flex:1, maxWidth:200, background:"linear-gradient(to right,transparent,#C9A84C44)" }}/>
        <span style={{ fontFamily:"Cinzel, serif", color:"#C9A84C88", fontSize:"clamp(10px,1vw,12px)", letterSpacing:5 }}>
          KROK {stepIdx+1} / {total}
        </span>
        <div style={{ height:1, flex:1, maxWidth:200, background:"linear-gradient(to left,transparent,#C9A84C44)" }}/>
      </div>
      <h1 className="gold-text" style={{ fontFamily:"Cinzel, serif", fontSize:"clamp(28px,4vw,54px)", fontWeight:900, marginBottom:4, lineHeight:1.15 }}>
        {step.nazev}
      </h1>
      <p style={{ fontFamily:"Lora, serif", fontSize:"clamp(14px,1.5vw,20px)", color:"#F5E6C8", opacity:0.8, fontStyle:"italic", marginBottom:8 }}>
        {step.podnadpis}
      </p>
      <div style={{ display:"flex", alignItems:"center", justifyContent:"center", gap:16, marginTop:6 }}>
        <OrnamentalDivider size="sm"/>
        <span style={{ fontSize:"clamp(24px,2.5vw,36px)" }}>{step.emoji}</span>
        <OrnamentalDivider size="sm"/>
      </div>
    </div>
  );
}

function TVReadingContent({ step }) {
  return (
    <div style={{ padding:"1vh 6vw 2vh", maxWidth:920, margin:"0 auto", width:"100%" }}>
      <p style={{ fontFamily:"Lora, serif", fontSize:"clamp(15px,1.4vw,20px)", lineHeight:1.9, color:"#F5E6C8", textAlign:"center", marginBottom:"2vh" }}>
        {step.introText}
      </p>
      {step.citaceHagady && (
        <div style={{ background:"rgba(201,168,76,0.08)", border:"1px solid rgba(201,168,76,0.35)", borderLeft:"4px solid #C9A84C", borderRadius:10, padding:"16px 24px", marginBottom:"1.5vh" }}>
          <p style={{ fontFamily:"Crimson Pro, serif", fontSize:"clamp(14px,1.3vw,18px)", fontStyle:"italic", color:"#E8C878", lineHeight:1.8 }}>
            {step.citaceHagady}
          </p>
        </div>
      )}
      <div style={{ display:"flex", alignItems:"center", justifyContent:"center", gap:12, padding:"12px 20px", background:"rgba(255,255,255,0.04)", border:"1px solid rgba(255,255,255,0.07)", borderRadius:10 }}>
        <span style={{ fontSize:20 }}>{step.typAktivity==="info"?"🕯️":({kvíz:"🎯",postřeh:"⚡",seřazování:"🔀",reflexe:"💭",anketa:"💬",hlasování:"🗳️",psaní:"⌨️",pohár:"🍷"})[step.typAktivity]||""}</span>
        <span style={{ fontFamily:"Lora, serif", fontSize:"clamp(13px,1.1vw,16px)", color:"#C9A84C", fontStyle:"italic" }}>
          {step.typAktivity==="info"?"Žádná aktivita — pouze čtení":("Připravte se na: "+ACTIVITY_LABELS[step.typAktivity])}
        </span>
      </div>
    </div>
  );
}

function TVGameArea({ step, gameActive, gameState, submittedWords, players }) {
  const activity = step.aktivity;
  // Považujeme odpovědi za odhalené, když hra skončila nebo všichni odpověděli
  const answeredCount = Object.keys(gameState.answers||{}).length;
  const allAnswered = players.length > 0 && players.every(p => p.answered);
  const showCorrect = allAnswered;

  const EMOJI_MAP = {kvíz:"🎯",postřeh:"⚡",seřazování:"🔀",reflexe:"💭",anketa:"💬",hlasování:"🗳️",psaní:"⌨️",pohár:"🍷"};

  if (!gameActive) return (
    <div style={{ textAlign:"center", padding:"5vh 40px" }}>
      <div style={{ fontSize:"clamp(36px,5vw,64px)", marginBottom:12 }}>
        {EMOJI_MAP[step.typAktivity]||"🎮"}
      </div>
      <div style={{ fontFamily:"Cinzel, serif", color:"#C9A84C", fontSize:"clamp(16px,2vw,26px)", marginBottom:8 }}>
        {ACTIVITY_LABELS[step.typAktivity]||step.typAktivity} — Připravte se!
      </div>
      <div style={{ fontFamily:"Lora, serif", color:"rgba(245,230,200,0.5)", fontSize:"clamp(13px,1.1vw,16px)" }}>
        Moderátor zahájí aktivitu...
      </div>
    </div>
  );

  // QUIZ
  if (step.typAktivity === "kvíz") {
    const qIdx = gameState.questionIndex||0;
    const q = activity.otazky?.[qIdx];
    if (!q) return null;
    return (
      <div style={{ padding:"1vh 4vw", maxWidth:880, margin:"0 auto", width:"100%" }}>
        {activity.otazky.length>1 && (
          <div style={{ textAlign:"center", marginBottom:"1.5vh" }}>
            <span style={{ fontFamily:"Cinzel, serif", color:"#C9A84C88", fontSize:"clamp(10px,0.9vw,13px)", letterSpacing:3 }}>
              OTÁZKA {qIdx+1} / {activity.otazky.length}
            </span>
          </div>
        )}
        <p style={{ fontFamily:"Lora, serif", fontSize:"clamp(16px,1.6vw,22px)", color:"#F5E6C8", textAlign:"center", marginBottom:"2vh", lineHeight:1.55 }}>{q.otazka}</p>
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
          {q.moznosti.map((opt,i)=>(
            <div key={i} style={{
              padding:"clamp(12px,1.5vh,18px) clamp(14px,1.5vw,20px)", borderRadius:12,
              background:showCorrect?(i===q.spravna?"#16A34A":"rgba(255,255,255,0.05)"):QUIZ_COLORS[i].bg,
              opacity:showCorrect&&i!==q.spravna?0.3:1,
              border:`2px solid ${showCorrect&&i===q.spravna?"#4ADE80":"transparent"}`,
              display:"flex", alignItems:"center", gap:14, transition:"all 0.6s ease",
              boxShadow:showCorrect&&i===q.spravna?"0 0 28px rgba(74,222,128,0.45)":"none",
            }}>
              <span style={{ fontFamily:"Cinzel, serif", fontSize:"clamp(16px,1.8vw,22px)", fontWeight:900, color:"white", width:26, textAlign:"center" }}>{QUIZ_COLORS[i].label}</span>
              <span style={{ fontFamily:"Lora, serif", fontSize:"clamp(14px,1.2vw,18px)", color:"white", flex:1, lineHeight:1.4 }}>{opt}</span>
              {showCorrect&&i===q.spravna&&<span style={{ fontSize:22 }}>✓</span>}
            </div>
          ))}
        </div>
        <div style={{ textAlign:"center", marginTop:"1.5vh", fontFamily:"Cinzel, serif", color:"#C9A84C66", fontSize:"clamp(11px,0.9vw,13px)" }}>
          {answeredCount} / {players.length} odpovědělo
        </div>
      </div>
    );
  }

  // REACTION
  if (step.typAktivity === "postřeh") return (
    <div style={{ textAlign:"center", padding:"2vh 4vw" }}>
      <p style={{ fontFamily:"Lora, serif", fontSize:"clamp(15px,1.3vw,19px)", color:"#F5E6C8", marginBottom:"3vh", lineHeight:1.6, maxWidth:640, margin:"0 auto 3vh" }}>{activity.instrukce}</p>
      {gameState.reactionWinner ? (
        <div className="anim-pop" style={{ display:"inline-block", padding:"clamp(16px,2vh,28px) clamp(30px,3vw,52px)", borderRadius:18, background:"rgba(201,168,76,0.15)", border:"2px solid #C9A84C" }}>
          <div style={{ fontSize:"clamp(36px,4vw,52px)", marginBottom:8 }}>🏆</div>
          <div style={{ fontFamily:"Cinzel, serif", fontSize:"clamp(18px,2vw,28px)", color:"#E8C878", fontWeight:700 }}>Nejrychlejší: {gameState.reactionWinner}!</div>
        </div>
      ) : (
        <div style={{ width:"clamp(120px,14vw,190px)", height:"clamp(120px,14vw,190px)", borderRadius:"50%", margin:"0 auto",
          background:gameState.reactionActive?"radial-gradient(circle,#EF4444,#991B1B)":"radial-gradient(circle,#374151,#1F2937)",
          display:"flex", alignItems:"center", justifyContent:"center",
          boxShadow:gameState.reactionActive?"0 0 70px rgba(239,68,68,0.55)":"none",
          className:gameState.reactionActive?"reaction-anim":"", transition:"all 0.3s",
          fontSize:"clamp(36px,4vw,56px)" }}>
          {gameState.reactionActive?"⚡":"⏳"}
        </div>
      )}
      {gameState.reactionActive&&!gameState.reactionWinner&&(
        <div style={{ marginTop:"2vh", fontFamily:"Cinzel, serif", fontSize:"clamp(18px,2.5vw,30px)", color:"#EF4444", letterSpacing:4 }}>STISKNI TEĎ!</div>
      )}
    </div>
  );

  // WORDCLOUD
  if (step.typAktivity==="anketa"&&activity.typ==="wordcloud") return (
    <div style={{ padding:"1vh 4vw", textAlign:"center", maxWidth:900, margin:"0 auto", width:"100%" }}>
      <p style={{ fontFamily:"Crimson Pro, serif", fontSize:"clamp(15px,1.5vw,20px)", fontStyle:"italic", color:"#E8C878", marginBottom:"2vh", lineHeight:1.5 }}>"{activity.otazka}"</p>
      <div style={{ display:"flex", flexWrap:"wrap", gap:10, justifyContent:"center", minHeight:80 }}>
        {submittedWords.length===0&&<p style={{ color:"rgba(255,255,255,0.22)", fontFamily:"Lora, serif", fontSize:"clamp(14px,1.2vw,18px)", margin:"auto" }}>Čeká se na odpovědi hráčů...</p>}
        {submittedWords.map((w,i)=>(
          <span key={i} className="anim-word" style={{ animationDelay:`${i*0.06}s`, fontFamily:"Cinzel, serif",
            fontSize:`clamp(13px,${1.1+(i%3)*0.4}vw,${18+(i%3)*5}px)`,
            color:["#C9A84C","#E8C878","#F5E6C8","#A3C4BC","#D4B896"][i%5],
            padding:"6px 16px", borderRadius:24, background:"rgba(255,255,255,0.05)", border:"1px solid rgba(201,168,76,0.25)" }}>
            {w}
          </span>
        ))}
      </div>
    </div>
  );

  // SCALE
  if (step.typAktivity==="anketa"&&activity.typ==="scale") {
    const counts=activity.stupnice.map((_,i)=>Object.values(gameState.scaleAnswers||{}).filter(v=>v===i).length);
    const mx=Math.max(...counts,1);
    return (
      <div style={{ padding:"1vh 6vw", maxWidth:720, margin:"0 auto", width:"100%" }}>
        <p style={{ fontFamily:"Crimson Pro, serif", fontSize:"clamp(15px,1.4vw,20px)", fontStyle:"italic", color:"#E8C878", textAlign:"center", marginBottom:"2vh" }}>"{activity.otazka}"</p>
        {activity.stupnice.map((label,i)=>(
          <div key={i} style={{ display:"flex", alignItems:"center", gap:14, marginBottom:10 }}>
            <span style={{ fontFamily:"Lora, serif", fontSize:"clamp(14px,1.2vw,17px)", color:"#F5E6C8", width:220, textAlign:"right" }}>{label}</span>
            <div style={{ flex:1, height:28, borderRadius:6, background:"rgba(255,255,255,0.07)", overflow:"hidden" }}>
              <div style={{ width:`${(counts[i]/mx)*100}%`, height:"100%", background:`hsl(${30+i*16},80%,55%)`, borderRadius:6, transition:"width 0.5s ease" }}/>
            </div>
            <span style={{ fontFamily:"Cinzel, serif", color:"#C9A84C", fontSize:"clamp(14px,1.2vw,17px)", width:22 }}>{counts[i]}</span>
          </div>
        ))}
      </div>
    );
  }

  // SORTING — TV vždy zobrazuje správné pořadí jako referenci
  if (step.typAktivity==="seřazování") {
    const correctOrder = [...activity.polozky].sort((a,b)=>a.spravnaPozice-b.spravnaPozice);
    const answeredSorting = players.filter(p=>p.answered).length;
    return (
      <div style={{ padding:"1vh 6vw", maxWidth:700, margin:"0 auto", width:"100%" }}>
        <p style={{ fontFamily:"Lora, serif", fontSize:"clamp(14px,1.3vw,18px)", color:"#E8C878", textAlign:"center", marginBottom:"1vh", lineHeight:1.5 }}>{activity.instrukce}</p>
        <div style={{ textAlign:"center", marginBottom:"1.5vh", fontFamily:"Cinzel, serif", color:"#C9A84C88", fontSize:"clamp(10px,0.9vw,12px)", letterSpacing:2 }}>
          SPRÁVNÉ POŘADÍ &nbsp;·&nbsp; {answeredSorting} / {players.length} seřadilo
        </div>
        {correctOrder.map((item,i)=>(
          <div key={item.id} style={{ padding:"clamp(10px,1.2vh,14px) clamp(14px,1.5vw,20px)", marginBottom:8, background:"rgba(201,168,76,0.08)", border:"1px solid rgba(201,168,76,0.3)", borderRadius:10, display:"flex", alignItems:"center", gap:14 }}>
            <span style={{ fontFamily:"Cinzel, serif", color:"#C9A84C", fontSize:"clamp(15px,1.5vw,20px)", fontWeight:700, width:22 }}>{i+1}</span>
            <span style={{ fontFamily:"Lora, serif", color:"#F5E6C8", fontSize:"clamp(14px,1.2vw,18px)" }}>{item.text}</span>
          </div>
        ))}
      </div>
    );
  }

  // HLASOVÁNÍ (nový typ)
  if (step.typAktivity==="hlasování") {
    const counts = activity.moznosti.map((_,i)=>Object.values(gameState.scaleAnswers||{}).filter(v=>v===i).length);
    const mx = Math.max(...counts, 1);
    return (
      <div style={{ padding:"1vh 6vw", maxWidth:760, margin:"0 auto", width:"100%" }}>
        <p style={{ fontFamily:"Crimson Pro, serif", fontSize:"clamp(15px,1.4vw,20px)", fontStyle:"italic", color:"#E8C878", textAlign:"center", marginBottom:"2vh" }}>"{activity.otazka}"</p>
        {activity.moznosti.map((label,i)=>(
          <div key={i} style={{ display:"flex", alignItems:"center", gap:14, marginBottom:14 }}>
            <span style={{ fontFamily:"Lora, serif", fontSize:"clamp(13px,1.1vw,17px)", color:"#F5E6C8", width:240, textAlign:"right", flexShrink:0 }}>{label}</span>
            <div style={{ flex:1, height:32, borderRadius:8, background:"rgba(255,255,255,0.07)", overflow:"hidden" }}>
              <div style={{ width:`${(counts[i]/mx)*100}%`, height:"100%", background:`hsl(${200+i*30},80%,55%)`, borderRadius:8, transition:"width 0.5s ease" }}/>
            </div>
            <span style={{ fontFamily:"Cinzel, serif", color:"#C9A84C", fontSize:"clamp(14px,1.2vw,18px)", width:28, textAlign:"right" }}>{counts[i]}</span>
          </div>
        ))}
        <div style={{ textAlign:"center", marginTop:"1vh", fontFamily:"Cinzel, serif", color:"#C9A84C66", fontSize:"clamp(10px,0.9vw,12px)" }}>
          {Object.keys(gameState.scaleAnswers||{}).length} / {players.length} hlasovalo
        </div>
      </div>
    );
  }

  // POHÁR (Kadeš — prvních 5 kdo vypije)
  if (step.typAktivity==="pohár") {
    const CHALICE_POINTS = [30,25,20,15,10];
    const submissions = Object.values(gameState.typingSubmissions||{});
    return (
      <div style={{ textAlign:"center", padding:"2vh 4vw" }}>
        <div style={{ fontFamily:"Cinzel, serif", fontSize:"clamp(60px,9vw,110px)", marginBottom:"1.5vh" }}>🍷</div>
        <p style={{ fontFamily:"Lora, serif", color:"rgba(245,230,200,0.7)", fontSize:"clamp(14px,1.3vw,20px)", marginBottom:"3vh" }}>
          Prvních 5 hráčů, kteří vypijí pohár, získává bonusové body!
        </p>
        <div style={{ display:"flex", flexDirection:"column", gap:10, alignItems:"center", maxWidth:420, margin:"0 auto" }}>
          {CHALICE_POINTS.map((pts, i) => {
            const pid = submissions[i];
            const p = pid ? players.find(pl => pl.id === pid) : null;
            return (
              <div key={i} style={{ display:"flex", alignItems:"center", gap:14, padding:"10px 24px", borderRadius:12, width:"100%",
                background: p ? "rgba(201,168,76,0.15)" : "rgba(255,255,255,0.04)",
                border: p ? "1px solid rgba(201,168,76,0.45)" : "1px solid rgba(255,255,255,0.07)",
                transition:"all 0.4s" }}>
                <span style={{ fontSize:24, width:32 }}>{["🥇","🥈","🥉","4️⃣","5️⃣"][i]}</span>
                <span style={{ fontFamily:"Cinzel, serif", color:"#C9A84C", fontSize:"clamp(14px,1.3vw,18px)", flex:1, textAlign:"left" }}>
                  {p ? `${p.emoji} ${p.name}` : <span style={{ color:"rgba(255,255,255,0.2)" }}>čeká se…</span>}
                </span>
                <span style={{ fontFamily:"Cinzel, serif", color:p?"#E8C878":"rgba(255,255,255,0.15)", fontSize:"clamp(13px,1.2vw,17px)" }}>+{pts} b</span>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // PSANÍ (nový typ)
  if (step.typAktivity==="psaní") {
    const submissions = Object.values(gameState.typingSubmissions||{});
    return (
      <div style={{ textAlign:"center", padding:"2vh 4vw" }}>
        <div style={{ fontFamily:"Cinzel, serif", fontSize:"clamp(36px,6vw,80px)", color:"#E8C878", letterSpacing:10, marginBottom:"1.5vh", textShadow:"0 0 40px rgba(232,200,120,0.5)" }}>
          {activity.slovo}
        </div>
        <p style={{ fontFamily:"Lora, serif", color:"rgba(245,230,200,0.6)", fontSize:"clamp(14px,1.2vw,18px)", marginBottom:"2.5vh" }}>
          Napište toto slovo co nejrychleji!
        </p>
        <div style={{ fontFamily:"Cinzel, serif", color:"#C9A84C", fontSize:"clamp(18px,2vw,28px)", marginBottom:"2vh" }}>
          {submissions.length} / {players.length} odeslalo
        </div>
        {submissions.length>0&&(
          <div style={{ display:"flex", flexDirection:"column", gap:8, alignItems:"center" }}>
            {submissions.map((pid,i)=>{
              const p=players.find(pl=>pl.id===pid);
              return p?(
                <div key={i} className="anim-pop" style={{ animationDelay:`${i*0.1}s`, display:"flex", alignItems:"center", gap:12, padding:"10px 28px", borderRadius:12, background:"rgba(201,168,76,0.12)", border:"1px solid rgba(201,168,76,0.4)" }}>
                  <span style={{ fontSize:22 }}>{["🥇","🥈","🥉"][i]||`${i+1}.`}</span>
                  <span style={{ fontSize:22 }}>{p.emoji}</span>
                  <span style={{ fontFamily:"Lora, serif", color:"#F5E6C8", fontSize:"clamp(14px,1.2vw,18px)" }}>{p.name}</span>
                </div>
              ):null;
            })}
          </div>
        )}
      </div>
    );
  }

  // REFLEXE
  if (step.typAktivity==="reflexe") return (
    <div style={{ padding:"1vh 4vw", textAlign:"center", maxWidth:900, margin:"0 auto", width:"100%" }}>
      <p style={{ fontFamily:"Crimson Pro, serif", fontSize:"clamp(16px,1.8vw,26px)", fontStyle:"italic", color:"#E8C878", marginBottom:"2.5vh", lineHeight:1.65 }}>"{activity.otazka}"</p>
      <div style={{ display:"flex", flexWrap:"wrap", gap:12, justifyContent:"center" }}>
        {submittedWords.length===0&&<p style={{ color:"rgba(255,255,255,0.22)", fontFamily:"Lora, serif", fontSize:"clamp(14px,1.2vw,17px)" }}>Hráči sdílejí své myšlenky...</p>}
        {submittedWords.map((w,i)=>(
          <div key={i} className="anim-word" style={{ animationDelay:`${i*0.08}s`, padding:"10px 18px", borderRadius:10, background:"rgba(201,168,76,0.08)", border:"1px solid rgba(201,168,76,0.22)", fontFamily:"Lora, serif", fontSize:"clamp(13px,1.1vw,16px)", color:"#F5E6C8", fontStyle:"italic" }}>"{w}"</div>
        ))}
      </div>
    </div>
  );

  return null;
}

function TVScoreBar({ players, onlineMap = {} }) {
  const sorted=[...players].sort((a,b)=>b.score-a.score);
  if(!sorted.length) return null;
  return (
    <div style={{ position:"fixed", bottom:0, left:0, right:0, background:"rgba(6,13,46,0.97)", borderTop:"1px solid rgba(201,168,76,0.25)", padding:"8px 40px", display:"flex", gap:28, overflowX:"auto", backdropFilter:"blur(12px)", zIndex:50, alignItems:"center" }}>
      <span style={{ fontFamily:"Cinzel, serif", color:"#C9A84C66", fontSize:"clamp(9px,0.7vw,11px)", letterSpacing:4, whiteSpace:"nowrap" }}>SKÓRE</span>
      {sorted.map((p,i)=>(
        <div key={p.id} style={{ display:"flex", alignItems:"center", gap:7, whiteSpace:"nowrap" }}>
          {i===0&&<span style={{ fontSize:14 }}>🥇</span>}
          <span style={{ fontSize:18 }}>{p.emoji}</span>
          <span style={{ fontFamily:"Lora, serif", color:i===0?"#E8C878":"#F5E6C8", fontSize:"clamp(12px,1vw,15px)" }}>{p.name}</span>
          <span style={{ fontFamily:"Cinzel, serif", color:"#C9A84C", fontSize:"clamp(13px,1.1vw,16px)", fontWeight:700 }}>{p.score}</span>
          <div style={{ width:6, height:6, borderRadius:"50%", background:onlineMap[p.id]?"#4ADE80":"#EF4444", boxShadow:onlineMap[p.id]?"0 0 5px #4ADE80":"none" }}/>
        </div>
      ))}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// 9. ADMIN PANEL
// ═══════════════════════════════════════════════════════════
function AdminPanel({ stepIdx, total, sederMode, gameActive, gameState, activity, typAktivity, onPrev, onNext, onModeToggle, onStartGame, onNextQuestion, onClose }) {
  // hasNextQ — nevyžaduje gameState.revealed, protože tlačítko Odhalit bylo odstraněno
  const isInfo = typAktivity === "info";
  const hasNextQ = sederMode==="game"&&gameActive&&activity?.typ==="quiz"&&(activity.otazky?.length||0)>1&&(gameState.questionIndex||0)<((activity.otazky?.length||1)-1);
  const Btn = ({children,onClick,disabled,col}) => (
    <button onClick={onClick} disabled={disabled} className="touch-btn" style={{
      padding:"8px 13px", borderRadius:8, border:`1px solid ${col||"rgba(201,168,76,0.45)"}`,
      background:disabled?"rgba(255,255,255,0.02)":(col?"rgba("+hexToRgb(col)+",0.15)":"rgba(201,168,76,0.1)"),
      color:disabled?"#444":(col||"#E8C878"), cursor:disabled?"not-allowed":"pointer",
      fontFamily:"Cinzel, serif", fontSize:11, fontWeight:700, transition:"all 0.15s", whiteSpace:"nowrap",
    }}>{children}</button>
  );
  return (
    <div style={{ position:"fixed", bottom:35, left:"50%", transform:"translateX(-50%)", background:"rgba(4,10,40,0.97)", border:"1.5px solid #C9A84C", borderRadius:14, padding:"12px 18px", zIndex:999, boxShadow:"0 8px 48px rgba(0,0,0,0.8)", display:"flex", alignItems:"center", gap:8, backdropFilter:"blur(16px)", flexWrap:"wrap", maxWidth:"96vw" }}>
      <span style={{ fontFamily:"Cinzel, serif", color:"#C9A84C66", fontSize:10, marginRight:4, whiteSpace:"nowrap" }}>⚙️ ADMIN {stepIdx+1}/{total}</span>
      <Btn disabled={stepIdx===0} onClick={onPrev}>◀ Předch.</Btn>
      <Btn onClick={isInfo?undefined:onModeToggle} disabled={isInfo} col={sederMode==="game"?"#16A34A":"#2563EB"}>{sederMode==="reading"?"🎮 Spustit hru":"📖 Čtení"}</Btn>
      {sederMode==="game"&&!gameActive&&!isInfo&&<Btn onClick={onStartGame} col="#DC2626">▶ START!</Btn>}
      {hasNextQ&&<Btn onClick={onNextQuestion} col="#8B5CF6">Další otázka →</Btn>}
      <Btn disabled={stepIdx===total-1} onClick={onNext}>Další ▶</Btn>
      <button onClick={onClose} style={{ background:"none", border:"none", color:"#555", cursor:"pointer", fontSize:18, padding:"4px 6px", lineHeight:1 }}>×</button>
    </div>
  );
}
function hexToRgb(hex){
  const r=parseInt(hex.slice(1,3),16),g=parseInt(hex.slice(3,5),16),b=parseInt(hex.slice(5,7),16);
  return `${r},${g},${b}`;
}

// ═══════════════════════════════════════════════════════════
// 10. HOST APP
// ═══════════════════════════════════════════════════════════
function HostApp({ syncState, syncActions }) {
  const { phase, players, stepIdx, sederMode, gameActive, gameState, submittedWords } = syncState;
  const { startSeder, goToStep, setMode, startGame, nextQuestion } = syncActions;
  const [showAdmin, setShowAdmin] = useState(false);
  const [onlineMap, setOnlineMap] = useState({});

  useEffect(() => {
    const presRef = ref(db, "presence");
    const unsub = onValue(presRef, snap => setOnlineMap(snap.val() || {}));
    return () => unsub();
  }, []);
  const step = SEDER[stepIdx];

  // Klávesová zkratka pro Admin panel
  useEffect(() => {
    const h = e => { if(e.ctrlKey&&e.key==="a"){ e.preventDefault(); setShowAdmin(p=>!p); } };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, []);

  if (phase === "lobby") {
    return (
      <div style={{ width:"100vw", height:"100vh", overflow:"hidden", background:"radial-gradient(ellipse at 30% 15%,#1A2D6B 0%,#0D1B4B 50%,#060D2E 100%)" }}>
        <TVLobby players={players} onStart={startSeder} onlineMap={onlineMap} />
        {/* Tlačítko pro přidání testovacích hráčů */}
        <button onClick={()=>{ ["Patrik","Anna","Tomáš","Marie","Josef"].forEach((n,i)=>syncActions.joinPlayer(n,BIBLICAL_CHARS[i].emoji)); }} style={{ position:"fixed", bottom:16, right:16, padding:"8px 14px", borderRadius:8, background:"rgba(201,168,76,0.15)", border:"1px solid rgba(201,168,76,0.4)", color:"#C9A84C", cursor:"pointer", fontFamily:"Cinzel, serif", fontSize:11, fontWeight:700, zIndex:100 }}>
          + Demo hráči
        </button>
      </div>
    );
  }

  return (
    <div style={{ width:"100vw", height:"100vh", overflow:"hidden", background:"radial-gradient(ellipse at 28% 10%,#1A2D6B 0%,#0D1B4B 45%,#060D2E 100%)" }}>
      <TVStarfield />
      <div style={{ position:"relative", zIndex:1, height:"100%", display:"flex", flexDirection:"column", paddingBottom:52 }}>
        <TVProgressBar current={stepIdx} total={SEDER.length} />

        {/* Režim badge */}
        <div style={{ textAlign:"center", marginTop:8, marginBottom:2 }}>
          <span style={{ display:"inline-block", padding:"4px 18px", borderRadius:20, fontFamily:"Cinzel, serif", fontSize:"clamp(9px,0.8vw,11px)", letterSpacing:3,
            background:sederMode==="game"?"rgba(22,163,74,0.18)":"rgba(37,99,235,0.18)",
            border:`1px solid ${sederMode==="game"?"#16A34A66":"#2563EB66"}`,
            color:sederMode==="game"?"#4ADE80":"#93C5FD" }}>
            {sederMode==="reading"?"📖 REŽIM ČTENÍ":`🎮 HERNÍ REŽIM — ${ACTIVITY_LABELS[step.typAktivity]}`}
          </span>
        </div>

        <div key={stepIdx} className="anim-step" style={{ flex:1, overflowY:"auto", display:"flex", flexDirection:"column" }}>
          <TVHeader step={step} stepIdx={stepIdx} total={SEDER.length} />
          <div style={{ flex:1 }}>
            {sederMode==="reading"
              ? <TVReadingContent step={step} />
              : <TVGameArea step={step} gameActive={gameActive} gameState={gameState} submittedWords={submittedWords} players={players} />
            }
          </div>
        </div>
      </div>

      <TVScoreBar players={players} onlineMap={onlineMap} />

      {showAdmin && (
        <AdminPanel
          stepIdx={stepIdx} total={SEDER.length}
          sederMode={sederMode} gameActive={gameActive}
          gameState={gameState} activity={step.aktivity} typAktivity={step.typAktivity}
          onPrev={()=>goToStep(Math.max(0,stepIdx-1))}
          onNext={()=>goToStep(Math.min(SEDER.length-1,stepIdx+1))}
          onModeToggle={()=>setMode(sederMode==="reading"?"game":"reading")}
          onStartGame={startGame}
          onNextQuestion={nextQuestion}
          onClose={()=>setShowAdmin(false)}
        />
      )}

      {/* Floating admin toggle */}
      <button onClick={()=>setShowAdmin(p=>!p)} style={{ position:"fixed", top:25, right:12, padding:"7px 12px", borderRadius:8, background:showAdmin?"#8B6914":"rgba(4,10,40,0.8)", border:"1px solid rgba(201,168,76,0.4)", color:"#C9A84C", cursor:"pointer", fontFamily:"Cinzel, serif", fontSize:11, fontWeight:700, zIndex:200, backdropFilter:"blur(8px)" }}>
        ⚙️ Admin
      </button>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// 11. PLAYER KOMPONENTY — Registrace
// ═══════════════════════════════════════════════════════════
function PlayerRegistration({ onJoin }) {
  const [name, setName] = useState("");
  const [selectedChar, setSelectedChar] = useState(null);
  const [joining, setJoining] = useState(false);

  const handleJoin = () => {
    if (!name.trim() || !selectedChar) return;
    setJoining(true);
    setTimeout(() => onJoin(name.trim(), selectedChar.emoji), 600); // simulace latence
  };

  return (
    <div style={{ height:"100dvh", background:"linear-gradient(165deg,#F5E6C8 0%,#EDD9A3 40%,#E0C880 100%)", display:"flex", flexDirection:"column", overflow:"hidden" }}>
      {/* Hlavička */}
      <div style={{ background:"#0D1B4B", padding:"20px 20px 16px", textAlign:"center", flexShrink:0 }}>
        <div style={{ fontFamily:"Cinzel, serif", color:"#C9A84C", fontSize:11, letterSpacing:5, marginBottom:4 }}>✡️ PESACHOVÝ SEDER ✡️</div>
        <div style={{ fontFamily:"Cinzel, serif", color:"white", fontSize:22, fontWeight:900 }}>Zaregistruj se</div>
        <div style={{ fontFamily:"Lora, serif", color:"rgba(245,230,200,0.6)", fontSize:12, fontStyle:"italic", marginTop:4 }}>Vyber si svou biblickou postavu</div>
      </div>

      <div style={{ flex:1, overflowY:"auto", padding:"20px 18px 12px" }}>
        {/* Jméno */}
        <div style={{ marginBottom:20 }}>
          <div style={{ fontFamily:"Cinzel, serif", color:"#5A3A10", fontSize:11, letterSpacing:3, marginBottom:8 }}>TVOJE JMÉNO</div>
          <input
            value={name}
            onChange={e=>setName(e.target.value)}
            placeholder="Napiš své jméno..."
            maxLength={20}
            style={{ width:"100%", padding:"14px 16px", borderRadius:12, border:"2px solid rgba(13,27,75,0.25)", fontFamily:"Lora, serif", fontSize:18, color:"#1A0A00", background:"white", outline:"none", boxSizing:"border-box" }}
            onKeyDown={e=>e.key==="Enter"&&handleJoin()}
          />
        </div>

        {/* Výběr biblické postavy */}
        <div style={{ marginBottom:20 }}>
          <div style={{ fontFamily:"Cinzel, serif", color:"#5A3A10", fontSize:11, letterSpacing:3, marginBottom:12 }}>BIBLICKÁ POSTAVA</div>
          <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:8 }}>
            {BIBLICAL_CHARS.map((char)=>{
              const sel=selectedChar?.name===char.name;
              return (
                <button key={char.name} onClick={()=>setSelectedChar(char)} className="touch-btn" style={{
                  padding:"12px 6px", borderRadius:12, border:`2px solid ${sel?"#0D1B4B":"rgba(13,27,75,0.15)"}`,
                  background:sel?"#0D1B4B":"white",
                  cursor:"pointer", transition:"all 0.15s", display:"flex", flexDirection:"column", alignItems:"center", gap:4,
                  boxShadow:sel?"0 4px 16px rgba(13,27,75,0.25)":"0 2px 8px rgba(0,0,0,0.06)"
                }}>
                  <span style={{ fontSize:28 }}>{char.emoji}</span>
                  <span style={{ fontFamily:"Lora, serif", fontSize:12, color:sel?"white":"#1A0A00", fontWeight:sel?600:400 }}>{char.name}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Spodní část — náhled + tlačítko */}
      <div style={{ padding:"12px 18px", paddingBottom:"calc(20px + env(safe-area-inset-bottom, 0px))", background:"rgba(255,255,255,0.6)", borderTop:"1px solid rgba(13,27,75,0.12)", flexShrink:0 }}>
        {selectedChar && name.trim() && (
          <div className="anim-pop" style={{ display:"flex", alignItems:"center", gap:12, padding:"12px 16px", background:"rgba(13,27,75,0.06)", borderRadius:12, marginBottom:12, border:"1px solid rgba(13,27,75,0.12)" }}>
            <span style={{ fontSize:32 }}>{selectedChar.emoji}</span>
            <div>
              <div style={{ fontFamily:"Cinzel, serif", fontSize:16, color:"#0D1B4B", fontWeight:700 }}>{name.trim()}</div>
              <div style={{ fontFamily:"Lora, serif", fontSize:13, color:"#5A4020" }}>jako {selectedChar.name}</div>
            </div>
          </div>
        )}
        <button onClick={handleJoin} disabled={!name.trim()||!selectedChar||joining} className="touch-btn" style={{
          width:"100%", padding:"18px",
          background:name.trim()&&selectedChar?"linear-gradient(135deg,#0D1B4B,#1A2D6B)":"#C8B896",
          border:"none", borderRadius:14, fontFamily:"Cinzel, serif", fontSize:19, fontWeight:700,
          color:"white", cursor:name.trim()&&selectedChar?"pointer":"not-allowed",
          boxShadow:name.trim()&&selectedChar?"0 6px 22px rgba(13,27,75,0.35)":"none",
          transition:"all 0.2s", letterSpacing:1,
        }}>
          {joining?"Připojuji...":"Vstoupit do Sederu →"}
        </button>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// 12. PLAYER — Čekací obrazovka (Lobby)
// ═══════════════════════════════════════════════════════════
function PlayerWaiting({ player, players }) {
  const [dots, setDots] = useState(".");
  useEffect(()=>{
    const t=setInterval(()=>setDots(d=>d.length>=3?".":".."+d),600);
    return ()=>clearInterval(t);
  },[]);

  return (
    <div style={{ height:"100dvh", background:"linear-gradient(165deg,#F5E6C8 0%,#EDD9A3 100%)", display:"flex", flexDirection:"column" }}>
      <div style={{ background:"#0D1B4B", padding:"16px 20px", textAlign:"center" }}>
        <div style={{ fontFamily:"Cinzel, serif", color:"#C9A84C", fontSize:10, letterSpacing:5 }}>✡️ PESACHOVÝ SEDER ✡️</div>
      </div>

      <div style={{ flex:1, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", padding:24, textAlign:"center" }}>
        {/* Avatar */}
        <div className="breathe" style={{ fontSize:72, marginBottom:16 }}>{player.emoji}</div>
        <div style={{ fontFamily:"Cinzel, serif", color:"#0D1B4B", fontSize:24, fontWeight:900, marginBottom:4 }}>{player.name}</div>
        <div style={{ fontFamily:"Lora, serif", fontSize:14, color:"#5A4020", marginBottom:32, fontStyle:"italic" }}>jsi připojen/a ✓</div>

        <div style={{ background:"rgba(13,27,75,0.07)", borderRadius:16, padding:"20px 24px", border:"1px solid rgba(13,27,75,0.15)", marginBottom:28, maxWidth:360, width:"100%" }}>
          <div style={{ fontFamily:"Cinzel, serif", color:"#5A3A10", fontSize:11, letterSpacing:3, marginBottom:12 }}>ČEKÁ SE NA OSTATNÍ</div>
          <div style={{ display:"flex", flexWrap:"wrap", gap:8, justifyContent:"center" }}>
            {players.map(p=>(
              <div key={p.id} className="anim-conn" style={{ display:"flex", alignItems:"center", gap:6, padding:"6px 12px", background:"white", borderRadius:20, border:"1px solid rgba(13,27,75,0.15)", boxShadow:"0 2px 8px rgba(0,0,0,0.06)" }}>
                <span style={{ fontSize:18 }}>{p.emoji}</span>
                <span style={{ fontFamily:"Lora, serif", fontSize:13, color:"#1A0A00" }}>{p.name}</span>
              </div>
            ))}
          </div>
        </div>

        <div style={{ fontFamily:"Cinzel, serif", color:"#5A3A10", fontSize:14, letterSpacing:2 }}>
          Počkej na zahájení hospodářem{dots}
        </div>
        <div style={{ fontFamily:"Lora, serif", fontSize:13, color:"rgba(90,64,32,0.55)", marginTop:8, fontStyle:"italic" }}>
          Hagada bude číst moderátor na TV
        </div>
      </div>

      {/* Ozdoba */}
      <div style={{ padding:"16px", textAlign:"center", opacity:0.3 }}>
        <OrnamentalDivider />
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// 13. PLAYER — Herní ovladač (Game Controller)
// ═══════════════════════════════════════════════════════════
function PlayerController({ player, step, syncState, syncActions, onScoreboard }) {
  const { sederMode, gameActive, gameState } = syncState;
  const { submitQuizAnswer, submitWord, submitScale, submitReaction, submitSorting, submitTyping, submitChalice } = syncActions;
  const activity = step.aktivity;

  const [answered, setAnswered] = useState(false);
  const [localAnswer, setLocalAnswer] = useState(null);
  const [inputVal, setInputVal] = useState("");
  const [sortOrder, setSortOrder] = useState([]);

  useEffect(()=>{
    setAnswered(false); setLocalAnswer(null); setInputVal("");
    const polozky=step.aktivity?.polozky;
    setSortOrder(polozky?shuffleArr(polozky):[]);
  },[step.id]);

  // Reset answered při nové otázce
  useEffect(()=>{ setAnswered(false); setLocalAnswer(null); },[gameState.questionIndex]);

  // Detect if this player already answered (Firebase-ready: sledovat gameState.answers[player.id])
  const myAnswered = answered;
  const qIdx = gameState.questionIndex||0;
  const currentQ = activity?.otazky?.[qIdx];
  const myScore = player.score;
  const isWinner = gameState.reactionWinner === player.name;

  const vibrate=(pattern)=>{ if(navigator.vibrate) navigator.vibrate(pattern); };

  // Handlers
  const doQuiz=(i)=>{
    if(myAnswered) return;
    setAnswered(true); setLocalAnswer(i);
    submitQuizAnswer(player.id, i, i===currentQ?.spravna, currentQ?.body||15);
    vibrate(i===currentQ?.spravna?[40,30,80]:60);
  };

  const doText=()=>{
    if(!inputVal.trim()||myAnswered) return;
    setAnswered(true);
    submitWord(player.id, inputVal.trim(), activity?.body||10);
    setInputVal("");
    vibrate(50);
  };

  const doScale=(idx)=>{
    if(myAnswered) return;
    setAnswered(true);
    submitScale(player.id, idx, activity.stupnice[idx], activity?.body||5);
    vibrate(50);
  };

  const doReact=()=>{
    if(myAnswered||!gameState.reactionActive||gameState.reactionWinner) return;
    setAnswered(true);
    submitReaction(player.id);
    vibrate([60,40,120]);
  };

  const doSorting=()=>{
    if(myAnswered) return;
    setAnswered(true);
    const polozky=activity?.polozky||[];
    const correct=sortOrder.filter((it,idx)=>it.spravnaPozice===idx).length;
    const pts=Math.round((correct/polozky.length)*(activity?.body||20));
    submitSorting(player.id, pts);
    vibrate([40,20,40,20,80]);
  };

  const doVoting=(i)=>{
    if(myAnswered) return;
    setAnswered(true); setLocalAnswer(i);
    submitScale(player.id, i, activity?.moznosti?.[i]||"", activity?.body||10);
    vibrate(50);
  };

  const [typingError, setTypingError] = useState(false);
  const doTyping=()=>{
    if(!inputVal.trim()||myAnswered) return;
    const correct=(activity?.slovo||"").toUpperCase();
    if(inputVal.trim().toUpperCase()!==correct){
      setTypingError(true);
      setTimeout(()=>setTypingError(false),1200);
      vibrate(100);
      return;
    }
    setAnswered(true);
    submitTyping(player.id, inputVal.trim());
    vibrate([40,30,80]);
  };

  const moveItem=(idx,dir)=>{
    const a=[...sortOrder], t=idx+dir;
    if(t<0||t>=a.length) return;
    [a[idx],a[t]]=[a[t],a[idx]]; setSortOrder(a);
  };

  const isCorrect=localAnswer!==null&&currentQ&&localAnswer===currentQ.spravna;

  // -------- RENDER --------
  return (
    <div style={{ height:"100dvh", display:"flex", flexDirection:"column", background:"linear-gradient(165deg,#F5E6C8 0%,#EDD9A3 100%)", overflow:"hidden" }}>
      {/* Záhlaví */}
      <div style={{ background:"#0D1B4B", padding:"10px 16px", display:"flex", alignItems:"center", justifyContent:"space-between", flexShrink:0 }}>
        <div style={{ display:"flex", alignItems:"center", gap:10 }}>
          <span style={{ fontSize:26 }}>{player.emoji}</span>
          <div>
            <div style={{ fontFamily:"Cinzel, serif", color:"#C9A84C", fontSize:9, letterSpacing:3 }}>PESACH SEDER</div>
            <div style={{ fontFamily:"Cinzel, serif", color:"white", fontSize:15, fontWeight:700 }}>{player.name}</div>
          </div>
        </div>
        <div style={{ textAlign:"right" }}>
          <div style={{ color:"#C9A84C88", fontSize:9, fontFamily:"Cinzel, serif", letterSpacing:1 }}>SKÓRE</div>
          <div style={{ color:"#E8C878", fontSize:26, fontFamily:"Cinzel, serif", fontWeight:900 }}>{myScore}</div>
        </div>
      </div>

      {/* Krok info */}
      <div style={{ background:"rgba(13,27,75,0.07)", borderBottom:"1px solid rgba(13,27,75,0.12)", padding:"8px 16px 7px", flexShrink:0 }}>
        <div style={{ fontFamily:"Cinzel, serif", fontSize:12, color:"#0D1B4B", fontWeight:700 }}>{step.emoji} {step.nazev}</div>
        <div style={{ fontSize:11, color:"#5A4020", marginTop:1 }}>{step.podnadpis}</div>
      </div>

      {/* Obsah — POUZE herní ovladač, žádný text Hagady */}
      <div style={{ flex:1, overflowY:"auto", padding:"14px 16px", display:"flex", flexDirection:"column" }}>

        {/* ČTENÍ — hráč vidí jen instrukci pro svůj úkol */}
        {sederMode==="reading" && (
          <div style={{ flex:1, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", textAlign:"center" }}>
            <div style={{ fontSize:48, marginBottom:14 }}>{step.emoji}</div>
            <div style={{ background:"rgba(201,168,76,0.12)", border:"1px solid #C9A84C", borderRadius:14, padding:"16px 18px", maxWidth:340, marginBottom:16 }}>
              <div style={{ fontFamily:"Cinzel, serif", fontSize:9, color:"#8B6914", marginBottom:6, letterSpacing:2 }}>TVOJE ÚLOHA</div>
              <p style={{ fontFamily:"Lora, serif", fontSize:14, color:"#1A0A00", lineHeight:1.7 }}>{step.instrukceProHrace}</p>
            </div>
            <div style={{ fontFamily:"Cinzel, serif", color:"#8B6914", fontSize:10, letterSpacing:3, opacity:0.7 }}>
              {ACTIVITY_LABELS[step.typAktivity]}
            </div>
          </div>
        )}

        {/* HERNÍ REŽIM — čekání na spuštění */}
        {sederMode==="game"&&!gameActive&&(
          <div style={{ flex:1, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", textAlign:"center" }}>
            <div style={{ fontSize:52, marginBottom:12 }}>⏳</div>
            <div style={{ fontFamily:"Cinzel, serif", fontSize:18, color:"#0D1B4B", fontWeight:700 }}>Hra se připravuje…</div>
            <div style={{ color:"#5A4020", marginTop:8, fontSize:13 }}>Počkej na moderátora</div>
          </div>
        )}

        {/* UŽ ODPOVĚDĚL */}
        {sederMode==="game"&&gameActive&&myAnswered&&(
          <div className="anim-pop" style={{ flex:1, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", textAlign:"center" }}>
            <div style={{ fontSize:72, marginBottom:14 }}>
              {step.typAktivity==="kvíz"?(isCorrect?"✅":"❌"):step.typAktivity==="postřeh"?(isWinner?"🏆":"😅"):"✅"}
            </div>
            <div style={{ fontFamily:"Cinzel, serif", fontSize:21, color:"#0D1B4B", fontWeight:700, marginBottom:8 }}>
              {step.typAktivity==="kvíz"?(isCorrect?"Správně! 🎉":"Špatně...")
               :step.typAktivity==="postřeh"?(isWinner?"Byl/a jsi nejrychlejší!":"Někdo byl rychlejší...")
               :"Odesláno! ✓"}
            </div>
            {step.typAktivity==="kvíz"&&isCorrect&&<div style={{ fontFamily:"Cinzel, serif", color:"#C9A84C", fontSize:16 }}>+{currentQ?.body||15} bodů!</div>}
            <div style={{ color:"#5A4020", marginTop:12, fontSize:13, fontStyle:"italic" }}>Čeká se na ostatní hráče…</div>
          </div>
        )}

        {/* AKTIVNÍ HRA — herní ovladač */}
        {sederMode==="game"&&gameActive&&!myAnswered&&(
          <div style={{ flex:1 }}>

            {/* KVÍZ */}
            {step.typAktivity==="kvíz"&&currentQ&&(
              <div>
                <div style={{ background:"#0D1B4B", borderRadius:12, padding:"14px 16px", marginBottom:16, textAlign:"center" }}>
                  <p style={{ fontFamily:"Lora, serif", fontSize:15, color:"white", lineHeight:1.6 }}>{currentQ.otazka}</p>
                </div>
                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
                  {currentQ.moznosti.map((opt,i)=>(
                    <button key={i} onClick={()=>doQuiz(i)} className="touch-btn" style={{ padding:"16px 8px", borderRadius:14, border:"none", background:QUIZ_COLORS[i].bg, color:"white", cursor:"pointer", display:"flex", flexDirection:"column", alignItems:"center", gap:5, boxShadow:"0 4px 14px rgba(0,0,0,0.2)", minHeight:78 }}>
                      <span style={{ fontFamily:"Cinzel, serif", fontSize:22, fontWeight:900 }}>{QUIZ_COLORS[i].label}</span>
                      <span style={{ fontFamily:"Lora, serif", fontSize:12, lineHeight:1.4, textAlign:"center" }}>{opt}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* POSTŘEH */}
            {step.typAktivity==="postřeh"&&(
              <div style={{ display:"flex", flexDirection:"column", alignItems:"center", paddingTop:10 }}>
                <div style={{ background:"rgba(13,27,75,0.07)", borderRadius:12, padding:14, marginBottom:22, width:"100%", textAlign:"center" }}>
                  <p style={{ fontSize:14, color:"#1A0A00", lineHeight:1.65 }}>{activity.instrukce}</p>
                </div>
                <button onClick={doReact} className={`touch-btn${gameState.reactionActive?" reaction-anim":""}`} style={{
                  width:170, height:170, borderRadius:"50%", border:"none",
                  background:gameState.reactionActive?"linear-gradient(135deg,#DC2626,#991B1B)":"linear-gradient(135deg,#C9A84C,#8B6914)",
                  color:"white", cursor:"pointer", fontSize:52,
                  boxShadow:gameState.reactionActive?"0 0 50px rgba(220,38,38,0.5)":"0 8px 24px rgba(201,168,76,0.4)",
                  transition:"background 0.3s", display:"flex", alignItems:"center", justifyContent:"center",
                }}>
                  {gameState.reactionActive?"⚡":"👆"}
                </button>
                <div style={{ marginTop:18, color:gameState.reactionActive?"#DC2626":"#5A4020", fontSize:14, fontFamily:"Cinzel, serif", letterSpacing:gameState.reactionActive?2:0, fontWeight:gameState.reactionActive?700:400 }}>
                  {gameState.reactionActive?"STISKNI TEĎ!":"Čekej na signál…"}
                </div>
              </div>
            )}

            {/* POHÁR */}
            {step.typAktivity==="pohár"&&(
              <div style={{ display:"flex", flexDirection:"column", alignItems:"center", paddingTop:10 }}>
                <p style={{ fontSize:14, color:"#1A0A00", lineHeight:1.65, textAlign:"center", marginBottom:24 }}>
                  {step.instrukceProHrace}
                </p>
                <button onClick={()=>{ submitChalice(player.id); vibrate([60,40,120]); }} className="touch-btn" style={{
                  width:170, height:170, borderRadius:"50%", border:"none",
                  background:"linear-gradient(135deg,#8B1A1A,#C0392B)",
                  color:"white", cursor:"pointer", fontSize:64,
                  boxShadow:"0 0 40px rgba(192,57,43,0.5)",
                  display:"flex", alignItems:"center", justifyContent:"center",
                }}>
                  🍷
                </button>
                <div style={{ marginTop:18, color:"#8B1A1A", fontSize:14, fontFamily:"Cinzel, serif", fontWeight:700, letterSpacing:2 }}>
                  VYPIL/A JSEM!
                </div>
              </div>
            )}

            {/* WORDCLOUD / REFLEXE */}
            {((step.typAktivity==="anketa"&&activity.typ==="wordcloud")||step.typAktivity==="reflexe")&&(
              <div>
                <div style={{ background:"rgba(13,27,75,0.06)", border:"1px solid rgba(13,27,75,0.14)", borderRadius:12, padding:14, marginBottom:14 }}>
                  <p style={{ fontFamily:"Crimson Pro, serif", fontSize:16, fontStyle:"italic", color:"#0D1B4B", lineHeight:1.6, textAlign:"center" }}>"{activity.otazka}"</p>
                </div>
                <textarea value={inputVal} onChange={e=>setInputVal(e.target.value)} placeholder="Napiš svou odpověď..." style={{ width:"100%", padding:"14px", borderRadius:12, border:"2px solid rgba(13,27,75,0.22)", fontFamily:"Lora, serif", fontSize:15, lineHeight:1.6, background:"white", color:"#1A0A00", resize:"none", height:100, outline:"none", boxSizing:"border-box" }}/>
                <button onClick={doText} disabled={!inputVal.trim()} className="touch-btn" style={{ width:"100%", marginTop:10, padding:"16px", borderRadius:12, border:"none", background:inputVal.trim()?"linear-gradient(135deg,#0D1B4B,#1A2D6B)":"#C8B896", color:"white", cursor:inputVal.trim()?"pointer":"not-allowed", fontFamily:"Cinzel, serif", fontSize:16, fontWeight:700 }}>
                  Odeslat →
                </button>
              </div>
            )}

            {/* ŠKÁLA */}
            {step.typAktivity==="anketa"&&activity.typ==="scale"&&(
              <div>
                <div style={{ background:"rgba(13,27,75,0.06)", borderRadius:12, padding:"12px 14px", marginBottom:14, textAlign:"center" }}>
                  <p style={{ fontFamily:"Crimson Pro, serif", fontSize:16, fontStyle:"italic", color:"#0D1B4B", lineHeight:1.6 }}>{activity.otazka}</p>
                </div>
                <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
                  {activity.stupnice.map((opt,i)=>(
                    <button key={i} onClick={()=>doScale(i)} className="touch-btn" style={{ padding:"15px 18px", borderRadius:12, border:"2px solid rgba(13,27,75,0.18)", background:"white", color:"#1A0A00", cursor:"pointer", fontFamily:"Lora, serif", fontSize:17, textAlign:"left", transition:"all 0.12s" }}>
                      {opt}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* SEŘAZOVÁNÍ */}
            {step.typAktivity==="seřazování"&&(
              <div>
                <div style={{ background:"rgba(13,27,75,0.06)", borderRadius:12, padding:12, marginBottom:14 }}>
                  <p style={{ fontSize:13, color:"#1A0A00", lineHeight:1.65, textAlign:"center" }}>{activity.instrukce}</p>
                </div>
                {sortOrder.map((item,idx)=>(
                  <div key={item.id} style={{ display:"flex", alignItems:"center", gap:10, padding:"10px 12px", marginBottom:8, background:"white", borderRadius:12, border:"2px solid rgba(13,27,75,0.1)", boxShadow:"0 2px 8px rgba(0,0,0,0.05)" }}>
                    <span style={{ color:"#C9A84C", fontWeight:700, fontSize:15, minWidth:20 }}>{idx+1}.</span>
                    <span style={{ flex:1, fontFamily:"Lora, serif", fontSize:13, color:"#1A0A00", lineHeight:1.4 }}>{item.text}</span>
                    <div style={{ display:"flex", flexDirection:"column", gap:3 }}>
                      {[[-1,"▲"],[1,"▼"]].map(([dir,lbl])=>(
                        <button key={lbl} onClick={()=>moveItem(idx,dir)} className="touch-btn" disabled={(dir===-1&&idx===0)||(dir===1&&idx===sortOrder.length-1)} style={{ background:"none", border:"1px solid #ddd", borderRadius:5, cursor:"pointer", fontSize:13, padding:"3px 9px", color:"#555", opacity:(dir===-1&&idx===0)||(dir===1&&idx===sortOrder.length-1)?0.3:1 }}>{lbl}</button>
                      ))}
                    </div>
                  </div>
                ))}
                <button onClick={doSorting} className="touch-btn" style={{ width:"100%", padding:"15px", borderRadius:12, border:"none", background:"linear-gradient(135deg,#0D1B4B,#1A2D6B)", color:"white", cursor:"pointer", fontFamily:"Cinzel, serif", fontSize:16, fontWeight:700, marginTop:4 }}>
                  Potvrdit pořadí ✓
                </button>
              </div>
            )}

            {/* HLASOVÁNÍ */}
            {step.typAktivity==="hlasování"&&(
              <div>
                <div style={{ background:"rgba(13,27,75,0.06)", border:"1px solid rgba(13,27,75,0.14)", borderRadius:12, padding:"12px 14px", marginBottom:14, textAlign:"center" }}>
                  <p style={{ fontFamily:"Crimson Pro, serif", fontSize:16, fontStyle:"italic", color:"#0D1B4B", lineHeight:1.6 }}>"{activity.otazka}"</p>
                </div>
                <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
                  {(activity.moznosti||[]).map((opt,i)=>(
                    <button key={i} onClick={()=>doVoting(i)} className="touch-btn" style={{
                      padding:"15px 18px", borderRadius:12,
                      border:`2px solid ${localAnswer===i?"#0D1B4B":"rgba(13,27,75,0.18)"}`,
                      background:localAnswer===i?"#0D1B4B":"white",
                      color:localAnswer===i?"white":"#1A0A00",
                      cursor:"pointer", fontFamily:"Lora, serif", fontSize:16, textAlign:"left", transition:"all 0.12s"
                    }}>
                      {opt}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* PSANÍ */}
            {step.typAktivity==="psaní"&&(
              <div>
                <div style={{ background:"#0D1B4B", borderRadius:14, padding:"18px 16px", textAlign:"center", marginBottom:16 }}>
                  <div style={{ fontFamily:"Cinzel, serif", fontSize:34, color:"#E8C878", letterSpacing:8, marginBottom:4 }}>
                    {activity.slovo}
                  </div>
                  <div style={{ color:"rgba(255,255,255,0.45)", fontSize:12, fontFamily:"Lora, serif" }}>
                    Napiš toto slovo co nejrychleji!
                  </div>
                </div>
                {typingError&&(
                  <div className="anim-pop" style={{ background:"#FEE2E2", border:"2px solid #DC2626", borderRadius:10, padding:"10px 14px", marginBottom:10, textAlign:"center", color:"#991B1B", fontFamily:"Cinzel, serif", fontSize:14 }}>
                    Špatně! Zkus to znovu.
                  </div>
                )}
                <input
                  value={inputVal}
                  onChange={e=>setInputVal(e.target.value)}
                  onKeyDown={e=>e.key==="Enter"&&doTyping()}
                  placeholder={`Napiš "${activity.slovo}"...`}
                  autoCapitalize="none"
                  style={{ width:"100%", padding:"14px 16px", borderRadius:12, border:`2px solid ${typingError?"#DC2626":"rgba(13,27,75,0.22)"}`, fontFamily:"Cinzel, serif", fontSize:22, letterSpacing:4, textAlign:"center", outline:"none", boxSizing:"border-box", transition:"border-color 0.2s" }}
                />
                <button onClick={doTyping} disabled={!inputVal.trim()} className="touch-btn" style={{ width:"100%", marginTop:10, padding:16, borderRadius:12, border:"none", background:inputVal.trim()?"linear-gradient(135deg,#0D1B4B,#1A2D6B)":"#C8B896", color:"white", cursor:inputVal.trim()?"pointer":"not-allowed", fontFamily:"Cinzel, serif", fontSize:16, fontWeight:700 }}>
                  Odeslat ▶
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Zápatí — PERMANENTNÍ tlačítko průběžného pořadí */}
      <div style={{ padding:"10px 16px", paddingBottom:"calc(14px + env(safe-area-inset-bottom, 0px))", background:"rgba(13,27,75,0.97)", borderTop:"2px solid #C9A84C", flexShrink:0 }}>
        <button onClick={onScoreboard} className="touch-btn" style={{ width:"100%", padding:"15px", background:"linear-gradient(135deg,#C9A84C,#8B6914)", border:"none", borderRadius:12, fontFamily:"Cinzel, serif", fontSize:17, fontWeight:700, color:"#0D1B4B", cursor:"pointer", boxShadow:"0 4px 18px rgba(201,168,76,0.4)", display:"flex", alignItems:"center", justifyContent:"center", gap:8 }}>
          🏆 Průběžné pořadí
        </button>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// 14. PLAYER APP — orchestrace hráčových stavů
// ═══════════════════════════════════════════════════════════
function PlayerApp({ syncState, syncActions }) {
  const { phase, players, stepIdx } = syncState;
  const [player, setPlayer] = useState(null); // { id, name, emoji }
  const [showScoreboard, setShowScoreboard] = useState(false);

  // Obnov hráče z localStorage po refreshi
  useEffect(() => {
    const saved = localStorage.getItem("seder_player");
    if (!saved) return;
    try {
      const parsed = JSON.parse(saved);
      // Ověř, že hráč stále existuje v Firebase (players se načítají přes onValue)
      // players může být zatím prázdné — počkáme až přijdou data
      setPlayer(parsed);
    } catch { localStorage.removeItem("seder_player"); }
  }, []);

  // Registrace presence — Firebase onDisconnect automaticky označí hráče jako offline
  useEffect(() => {
    if (!player) return;
    const presRef = ref(db, `presence/${player.id}`);
    const connRef = ref(db, ".info/connected");
    const unsub = onValue(connRef, (snap) => {
      if (snap.val()) {
        onDisconnect(presRef).remove();
        set(presRef, true);
      }
    });
    return () => { unsub(); remove(presRef); };
  }, [player?.id]);

  // Pokud hráč není v Firebase (byl smazán / reset hry), odregistruj ho
  useEffect(() => {
    if (!player || players.length === 0) return;
    const exists = players.find(p => p.id === player.id);
    if (!exists) {
      localStorage.removeItem("seder_player");
      setPlayer(null);
    }
  }, [players, player]);

  // Sync hráčovo score z globálního stavu
  const livePlayer = player ? players.find(p=>p.id===player.id)||player : null;

  const handleJoin = (name, emoji) => {
    const id = syncActions.joinPlayer(name, emoji);
    const p = { id, name, emoji };
    localStorage.setItem("seder_player", JSON.stringify(p));
    setPlayer(p);
  };

  if (!player) return <PlayerRegistration onJoin={handleJoin} />;

  if (phase==="lobby") return <PlayerWaiting player={livePlayer||player} players={players} />;

  const step = SEDER[stepIdx];
  return (
    <>
      <PlayerController
        player={livePlayer||player}
        step={step}
        syncState={syncState}
        syncActions={syncActions}
        onScoreboard={()=>setShowScoreboard(true)}
      />
      {showScoreboard&&<ScoreboardOverlay players={players} onClose={()=>setShowScoreboard(false)}/>}
    </>
  );
}

// ═══════════════════════════════════════════════════════════
// 15. ROOT APP — role router
// ═══════════════════════════════════════════════════════════
export default function App() {
  // Inject global styles
  useEffect(()=>{
    const s=document.createElement("style");
    s.textContent=GLOBAL_STYLE;
    document.head.appendChild(s);
    return ()=>document.head.removeChild(s);
  },[]);

  const role = useDeviceRole();
  const { state: syncState, actions: syncActions } = useSyncEngine();

  // Loading — čeká se na detekci zařízení
  if (!role) return (
    <div style={{ width:"100vw", height:"100vh", background:"#060D2E", display:"flex", alignItems:"center", justifyContent:"center" }}>
      <div style={{ textAlign:"center" }}>
        <div style={{ fontSize:48, marginBottom:14 }}>✡️</div>
        <div style={{ fontFamily:"Cinzel, serif", color:"#C9A84C", fontSize:16, letterSpacing:4 }}>PESACH SEDER</div>
      </div>
    </div>
  );

  return (
    <div style={{ width:"100vw", height:"100dvh", overflow:"hidden" }}>
      {role==="HOST"
        ? <HostApp syncState={syncState} syncActions={syncActions} />
        : <PlayerApp syncState={syncState} syncActions={syncActions} />
      }
    </div>
  );
}
